import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { yearMonthBadge } from "@/lib/periodBadge";
import { useLocation } from "wouter";
import { useQuickCreate } from "@/lib/quickCreateContext";
import { useAIPageContext } from "@/lib/aiPageContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { moveTask, type MoveTarget } from "@/lib/taskSectionMove";
import { api, fetchWithAuth, Project, Account, ProjectTemplate, User, ProjectUser, ProjectTask, WorkflowPipeline, Tag, TaskSection, SectionReminderConfig } from "@/lib/api";
import { computeAutoCollapsedSectionIds } from "@/lib/sectionAutoCollapse";
import { WorkflowStatusBadge } from "@/components/custom/WorkflowStatusBadge";
import { TaskDetailRow } from "@/components/custom/TaskDetailRow";
import { format, parseISO, isBefore, startOfDay, isWithinInterval, addDays, endOfWeek } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Loader2, Plus, Search, Briefcase, Trash2, Users, X, ChevronRight, CheckCircle2, Clock, MessageSquare, Check, RefreshCw, ChevronDown, RotateCcw, ArrowLeft, GripVertical, Pencil, Copy, ExternalLink, Zap, ListPlus, Play, Tags, Maximize2, Bell, ArrowUpDown, SlidersHorizontal, Paperclip, Table2, List, ListOrdered } from "lucide-react";
import { ProjectsTable, type SortableColumn, type ProjectsTableGroup } from "@/components/custom/ProjectsTable";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { buildProjectDeleteWarning } from "@/lib/projectDeleteWarning";
import { ProjectMessagesSheet } from "@/components/custom/ProjectMessagesSheet";
import { SearchableSelect } from "@/components/custom/SearchableSelect";
import { useFilterPresets, PRESETS_KEY_PROJECTS } from "@/lib/filterPresets";
import { FilterPresetBar } from "@/components/custom/FilterPresetBar";
// Filter/sort/grouping shapes and the view-persistence rules live in the lib so they can be
// unit-tested without React — see projectsViewPersistence.test.ts.
import type {
  FilterField, FilterOperator, FilterRule, FilterGroup,
  SortField, ProjectsSortRule, GroupByPrimary, GroupBySecondary,
  StoredProjectsView, StoredColSort,
} from "@/lib/projectsViewPersistence";
import {
  DEFAULT_SORT_RULES, DEFAULT_GROUP_BY, DEFAULT_GROUP_BY_2,
  loadPersistedView, loadLegacyViewMode, serializeView, savePersistedView, sanitizeFilterGroups,
  hasActiveRules, countActiveRules,
} from "@/lib/projectsViewPersistence";
import { TagPicker, TagChip } from "@/components/custom/TagPicker";
import { projectTypeBadgeClasses, computePeriodLabel } from "@/lib/projectTypeColors";
import { reminderNextDisplay } from "@/lib/reminderNextDisplay";
import { buildProjectCloseWarning } from "@/lib/projectCloseWarning";
import { CompleteConfirmDialog } from "@/components/custom/CompleteConfirmDialog";
import { BulkCreateProjectsDialog } from "@/components/custom/BulkCreateProjectsDialog";
import { SectionAutomationsModal } from "@/components/custom/SectionAutomationsModal";
import { useTimerContext } from "@/lib/timerContext";
import { SendNotificationModal } from "@/components/custom/SendNotificationModal";
import { isCurrentAssignee, hasNoCurrentAssignee } from "@/lib/currentAssignee";
import { RECURRENCE_OPTIONS } from "@/lib/recurrenceOptions";

function triggerProjectConfetti(x: number, y: number) {
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;";
  document.body.appendChild(container);
  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#f97316"];
  for (let i = 0; i < 20; i++) {
    const dot = document.createElement("div");
    const color = colors[i % colors.length];
    const angle = (i / 20) * 360 + Math.random() * 18;
    const dist = 50 + Math.random() * 80;
    const dx = Math.cos((angle * Math.PI) / 180) * dist;
    const dy = Math.sin((angle * Math.PI) / 180) * dist - 30;
    const size = 6 + Math.random() * 6;
    dot.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:${size}px;background:${color};border-radius:50%;transform:translate(-50%,-50%);transition:all 0.7s ease-out;pointer-events:none;`;
    container.appendChild(dot);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dot.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        dot.style.opacity = "0";
      });
    });
  }
  setTimeout(() => document.body.removeChild(container), 800);
}

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  in_progress: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  on_hold: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  completed: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
};

const statusLabels: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  closed: "Closed",
  completed: "Completed",
};

const STATUS_ORDER = ["open", "in_progress", "on_hold", "closed", "completed"];
const TEXT_COLLATOR = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

const PERIOD_NAMES: Record<string, string> = {
  "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
};

// ─── Workflow pipeline UI ───────────────────────────────────────────────────

// (WorkflowStatusBadge and helpers moved to shared component: WorkflowStatusBadge.tsx)


interface SummaryCardProps {
  count: number;
  label: string;
  onClick?: () => void;
  active?: boolean;
}

function SummaryCard({ count, label, onClick, active }: SummaryCardProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all min-w-[140px] text-left ${
        active
          ? "bg-primary/10 border-primary shadow-sm"
          : "bg-card border-border hover:bg-muted/50 hover:border-muted-foreground/30"
      }`}
      data-testid={`summary-card-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <span className={`text-2xl font-bold ${active ? "text-primary" : "text-foreground"}`}>{count}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
      <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
    </button>
  );
}

function UserAvatar({ name, email, size = "sm" }: { name: string | null; email: string | null; size?: "sm" | "md" }) {
  const initials = name
    ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : email?.slice(0, 2).toUpperCase() || "??";
  const sizeClass = size === "md" ? "w-8 h-8" : "w-6 h-6";
  const textClass = size === "md" ? "text-sm" : "text-xs";
  // ring-background defines the circle edge against the row: the fill is pale enough that
  // without it a run of avatars reads as one shape. The assignee row that uses this spaces
  // them with gap rather than overlapping them, because 2-char initials clip under a stack.
  return (
    <Avatar className={`${sizeClass} shrink-0 ring-1 ring-background`} title={name ?? email ?? ""}>
      <AvatarFallback className={`bg-primary/10 text-primary font-medium ${textClass}`}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

// The table's column-header sort. Kept here rather than in the lib because it names a
// SortableColumn from the table component; the stored form is a bare string.
type ColSort = { col: SortableColumn | null; dir: "asc" | "desc" };
const EMPTY_FILTER_GROUPS: FilterGroup[] = [{ combinator: "AND", rules: [] }];

/** Narrow a stored column sort (bare string key) to the table's column union at the boundary. */
const toColSort = (cs: StoredColSort | ColSort | undefined): ColSort =>
  cs ? { col: (cs.col ?? null) as SortableColumn | null, dir: cs.dir } : { col: null, dir: "asc" };

// What a named preset stores: the filters plus the whole view arrangement, so applying one
// restores sort and grouping too. Every view field is optional — a preset saved before
// v1.5.28 has none of them, and applyFilters falls back to the shared defaults.
interface ProjectsFilters extends StoredProjectsView {
  searchQuery: string;
  projectStatusFilter: "active" | "all";
  hideFuture: boolean;
  filterGroups: FilterGroup[];
  colSort?: ColSort;
}

interface FilterMultiSelectProps {
  field: FilterField;
  values: string[];
  onChange: (values: string[]) => void;
  allUsers: User[];
  accounts: Account[];
  allTags: Tag[];
  clientGroups: { id: string; name: string; client_count?: number }[];
}

function FilterMultiSelect({ field, values, onChange, allUsers, accounts, allTags, clientGroups }: FilterMultiSelectProps) {
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);

  let options: { v: string; l: string }[] = [];

  if (field === "status") {
    options = [
      { v: "open", l: "Open" },
      { v: "in_progress", l: "In Progress" },
      { v: "on_hold", l: "On Hold" },
      { v: "closed", l: "Closed" },
      { v: "completed", l: "Completed" },
    ];
  } else if (field === "assignee") {
    options = [
      { v: "my_projects", l: "My Projects" },
      { v: "unassigned", l: "Unassigned" },
      ...allUsers.filter((u) => u.id).map((u) => ({ v: u.id, l: u.name || u.email })),
    ];
  } else if (field === "current_assignee") {
    options = [
      { v: "me", l: "Me (My Turn)" },
      { v: "unassigned", l: "Unassigned" },
      ...allUsers.filter((u) => u.id).map((u) => ({ v: u.id, l: u.name || u.email })),
    ];
  } else if (field === "client") {
    options = accounts.filter((a) => a.id).map((a) => ({ v: a.id, l: a.account_name }));
  } else if (field === "type") {
    options = [
      { v: "1040", l: "1040" },
      { v: "1120S", l: "1120S" },
      { v: "1065", l: "1065" },
      { v: "1120", l: "1120" },
      { v: "1041", l: "1041" },
      { v: "990", l: "990" },
      { v: "Bookkeeping", l: "Bookkeeping" },
      { v: "Financial Statements", l: "Financial Statements" },
      { v: "Property Tax", l: "Property Tax" },
      { v: "Sales Tax", l: "Sales Tax" },
      { v: "Payroll", l: "Payroll" },
      { v: "Advisory", l: "Advisory" },
    ];
  } else if (field === "year") {
    options = [
      { v: "2026", l: "2026" },
      { v: "2025", l: "2025" },
      { v: "2024", l: "2024" },
      { v: "2023", l: "2023" },
      { v: "2022", l: "2022" },
    ];
  } else if (field === "month") {
    options = [
      { v: "01", l: "Jan" }, { v: "02", l: "Feb" }, { v: "03", l: "Mar" },
      { v: "04", l: "Apr" }, { v: "05", l: "May" }, { v: "06", l: "Jun" },
      { v: "07", l: "Jul" }, { v: "08", l: "Aug" }, { v: "09", l: "Sep" },
      { v: "10", l: "Oct" }, { v: "11", l: "Nov" }, { v: "12", l: "Dec" },
    ];
  } else if (field === "tag") {
    options = allTags.map((t) => ({ v: t.id, l: t.name ?? "" }));
  } else if (field === "due_date") {
    options = [
      { v: "today", l: "Today" },
      { v: "this_week", l: "This Week" },
      { v: "next_week", l: "Next Week" },
      { v: "overdue", l: "Overdue" },
    ];
  } else if (field === "recurring") {
    options = [
      { v: "recurring", l: "Recurring" },
      { v: "one_time", l: "One-time" },
    ];
  } else if (field === "client_group") {
    options = clientGroups.map((g) => ({ v: g.id, l: g.name }));
  } else {
    return null;
  }

  const label =
    values.length === 0
      ? "Select…"
      : values.length <= 2
        ? values.map((v) => options.find((o) => o.v === v)?.l ?? v).join(", ")
        : `${values.length} selected`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`h-7 flex-1 text-xs border rounded px-2 text-left truncate min-w-0 ${values.length > 0 ? "text-foreground" : "text-muted-foreground"}`}
        >
          {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-56 overflow-y-auto">
        {options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.v}
            checked={values.includes(opt.v)}
            onCheckedChange={() => toggle(opt.v)}
          >
            {opt.l}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CopyBtn({ value }: { value: string }) {
  const { toast } = useToast();
  return (
    <button
      className="text-slate-500 hover:text-foreground transition-colors shrink-0 p-0.5 rounded hover:bg-muted"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(value); toast({ title: "Copied!" }); }}
      title="Copy to clipboard"
    >
      <Copy className="w-4 h-4" />
    </button>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const [bulkOpen, setBulkOpen] = useState(false);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const queryClient = useQueryClient();
  const { openSheetWithContext } = useTimerContext();
  const [, setLocation] = useLocation();

  // Navigation context passed via sessionStorage from AccountDetail
  const [backAccount, setBackAccount] = useState<{ id: string; name: string } | null>(null);

  // Filters. Every field below is seeded from the last-used view in localStorage so
  // the page comes back the way it was left. Lazy initializer so storage is read and
  // parsed once per mount, not on every render of this large, chatty component.
  const [persisted] = useState(loadPersistedView);
  const [searchQuery, setSearchQuery] = useState("");
  // Deliberately NOT restored. Unlike the rest of the view this is a data-volume switch, not a
  // display preference: "all" refetches every project ever (~6,800 vs ~436 active) and the server
  // computes per-project aggregates for all of them. Remembering one stray click would leave the
  // page permanently slow with no visible cause. Presets still carry it — that's a deliberate,
  // named choice rather than an accident.
  const [projectStatusFilter, setProjectStatusFilter] = useState<"active" | "all">("active");
  const [hideFuture, setHideFuture] = useState(persisted.hideFuture ?? true);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
    persisted.filterGroups?.length ? persisted.filterGroups : EMPTY_FILTER_GROUPS
  );
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [sortRules, setSortRules] = useState<ProjectsSortRule[]>(
    persisted.sortRules?.length ? persisted.sortRules : DEFAULT_SORT_RULES
  );
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupByPrimary>(persisted.groupBy ?? DEFAULT_GROUP_BY);
  const [expandedGroupsAlt, setExpandedGroupsAlt] = useState<Set<string>>(new Set());
  const [groupBy2, setGroupBy2] = useState<GroupBySecondary>(persisted.groupBy2 ?? DEFAULT_GROUP_BY_2);
  const [expandedGroupsAlt2, setExpandedGroupsAlt2] = useState<Set<string>>(new Set());
  const [focusProjectId, setFocusProjectId] = useState<string | null>(null);
  const [focusedProjectOverride, setFocusedProjectOverride] = useState<Project | null>(null);
  const [myWorkOnly, setMyWorkOnly] = useState(persisted.myWorkOnly ?? false);
  // Table (flat, FC-style) vs List (the by-client accordion). Table is the default birds-eye view.
  // Falls back to the pre-v1.5.28 standalone key so nobody's view flips on first load after deploy.
  const [viewMode, setViewMode] = useState<"table" | "list">(
    () => persisted.viewMode ?? loadLegacyViewMode() ?? "table"
  );
  // Column-header sort for the table; null = defer to the multi-level Sort panel.
  const [colSort, setColSort] = useState<ColSort>(toColSort(persisted.colSort));
  const handleSortColumn = (col: SortableColumn) =>
    setColSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  // Arriving via a deep-link from AccountDetail ("Back to <client>") overwrites the
  // filters with a single-client rule and forces List view. That's a transient focused
  // view, not a preference — persisting it would leave the user filtered to one client
  // the next time they open Projects. So writes are suppressed for such a visit, until
  // the user makes an explicit view choice (applies a preset or clears filters).
  const suppressPersist = useRef(false);

  // On mount: restore navigation context from AccountDetail and scroll position
  useEffect(() => {
    const raw = sessionStorage.getItem("projectsNav");
    if (raw) {
      suppressPersist.current = true;
      // The saved view must not be allowed to hide what the deep-link is pointing at.
      // forceExpandedAccounts is only consulted under client grouping (see the group
      // render below), so a saved "By Status" view would leave every group collapsed;
      // and My Work would filter the project out entirely if it isn't assigned to you.
      // Reset both for this visit only — writes are suppressed, so nothing is saved.
      setGroupBy(DEFAULT_GROUP_BY);
      setGroupBy2(DEFAULT_GROUP_BY_2);
      setMyWorkOnly(false);
      sessionStorage.removeItem("projectsNav");
      try {
        const { accountId, accountName, projectId, focusProjectId: focusId } = JSON.parse(raw);
        // A deep-link that targets a specific project needs the List (accordion) view —
        // only it expands a project inline to show its tasks. The Table view is flat and
        // would otherwise show the client's entire project list (incl. future recurring
        // spawns) instead of the clicked project's tasks. (B3 regression fix.)
        if (projectId || focusId) setViewMode("list");
        if (accountId) {
          setFilterGroups([{ combinator: "AND", rules: [{ field: "client", operator: "is", values: [accountId] }] }]);
          setForceExpandedAccounts(new Set([accountId]));
          setBackAccount({ id: accountId, name: accountName || "Client" });
        }
        if (projectId) setExpandedProjects(new Set([projectId]));
        if (focusId) setFocusProjectId(focusId);
      } catch { /* ignore malformed data */ }
    }

    const savedY = sessionStorage.getItem("projects-scroll-y");
    if (savedY) {
      sessionStorage.removeItem("projects-scroll-y");
      requestAnimationFrame(() => {
        document.getElementById("layout-scroll")?.scrollTo({ top: parseInt(savedY), behavior: "instant" as ScrollBehavior });
      });
    }

    return () => {
      const el = document.getElementById("layout-scroll");
      if (el && el.scrollTop > 0) {
        sessionStorage.setItem("projects-scroll-y", String(el.scrollTop));
      }
    };
  }, []);

  const hasActiveFilters =
    hasActiveRules(filterGroups) ||
    searchQuery !== "" ||
    !hideFuture;

  // Whether anything about the view differs from stock — filters, sort, grouping, My Work,
  // table-vs-list. Gates both "Reset View" and the preset save button, so anything you can
  // change is something you can save and something you can undo.
  const hasCustomView =
    hasActiveFilters ||
    myWorkOnly ||
    projectStatusFilter !== "active" ||
    viewMode !== "table" ||
    groupBy !== DEFAULT_GROUP_BY ||
    groupBy2 !== DEFAULT_GROUP_BY_2 ||
    colSort.col !== null ||
    sortRules.length !== 1 ||
    sortRules[0]?.field !== DEFAULT_SORT_RULES[0].field ||
    sortRules[0]?.dir !== DEFAULT_SORT_RULES[0].dir;

  // The view is remembered now, so navigating away no longer resets it — this button is the
  // only way back to stock. It therefore resets the whole arrangement, not just the filters:
  // grouping, sort, My Work, future/closed toggles and table-vs-list all return to default.
  const resetView = () => {
    suppressPersist.current = false;
    setSearchQuery("");
    setProjectStatusFilter("active");
    setHideFuture(true);
    setFilterGroups(EMPTY_FILTER_GROUPS);
    setMyWorkOnly(false);
    setSortRules(DEFAULT_SORT_RULES);
    setGroupBy(DEFAULT_GROUP_BY);
    setGroupBy2(DEFAULT_GROUP_BY_2);
    setViewMode("table");
    setColSort(toColSort(undefined));
  };

  // Filter presets
  const { presets: filterPresets, save: savePreset, remove: removePreset, rename: renamePreset } = useFilterPresets<ProjectsFilters>(PRESETS_KEY_PROJECTS);
  const currentFilters = useMemo<ProjectsFilters>(() => ({
    searchQuery, projectStatusFilter, hideFuture, filterGroups, myWorkOnly,
    sortRules, groupBy, groupBy2, viewMode, colSort,
  }), [searchQuery, projectStatusFilter, hideFuture, filterGroups, myWorkOnly,
       sortRules, groupBy, groupBy2, viewMode, colSort]);

  // Remember the last-used view. searchQuery is deliberately excluded: a search is a
  // one-off lookup, and restoring it would silently hide most projects on the next visit.
  // Presets still capture it, because there the user named the arrangement on purpose.
  // Serializing here (rather than depending on `currentFilters`) also keeps typing in the
  // search box from rewriting storage on every keystroke — the string simply doesn't change.
  const persistedViewJson = useMemo(
    () => serializeView({
      hideFuture, filterGroups, myWorkOnly,
      sortRules, groupBy, groupBy2, viewMode, colSort,
    }),
    [hideFuture, filterGroups, myWorkOnly,
     sortRules, groupBy, groupBy2, viewMode, colSort]
  );

  useEffect(() => {
    if (suppressPersist.current || persistedViewJson === null) return;
    savePersistedView(persistedViewJson);
  }, [persistedViewJson]);

  // Presets also come out of localStorage, so treat every field as optional at runtime
  // no matter what the type says — a preset saved by an older build is missing whatever
  // that build didn't have. Anything absent resets to its default, so applying a preset
  // always lands on a fully-defined view rather than keeping stale bits of the last one.
  function applyFilters(f: ProjectsFilters) {
    suppressPersist.current = false;
    setSearchQuery(f.searchQuery ?? "");
    setProjectStatusFilter(f.projectStatusFilter === "all" ? "all" : "active");
    setHideFuture(f.hideFuture ?? true);
    // Validated, not trusted: a preset is localStorage like anything else, and a group
    // missing its `rules` array would throw on the next render.
    setFilterGroups(sanitizeFilterGroups(f.filterGroups) ?? EMPTY_FILTER_GROUPS);
    setMyWorkOnly(f.myWorkOnly ?? false);
    setSortRules(f.sortRules?.length ? f.sortRules : DEFAULT_SORT_RULES);
    setGroupBy(f.groupBy ?? DEFAULT_GROUP_BY);
    setGroupBy2(f.groupBy2 ?? DEFAULT_GROUP_BY_2);
    setViewMode(f.viewMode === "list" ? "list" : "table");
    setColSort(toColSort(f.colSort));
  }

  const { trigger, setTrigger } = useQuickCreate();
  useEffect(() => {
    if (trigger === "new-project") { setModalOpen(true); setTrigger(null); }
  }, [trigger]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    account_id: "",
    status: "open",
    start_date: "",
    due_date: "",
    internal_due_date: "",
    template_id: "",
    recurring_pattern: "",
    next_spawn_date: "",
    project_type: "",
    tax_year: "",
    period_label: "",
    accounting_period_date: "",
  });
  const [assignedUsers, setAssignedUsers] = useState<ProjectUser[]>([]);
  const [newUserId, setNewUserId] = useState("");
  const [newUserRole, setNewUserRole] = useState("agent");

  // Project messages sheet
  const [messagesProject, setMessagesProject] = useState<{ id: string; title: string } | null>(null);

  // Lazy task progress: projectId → { total, done }
  const [taskProgressMap, setTaskProgressMap] = useState<Map<string, { total: number; done: number }>>(new Map());
  const fetchedProjectsRef = useRef<Set<string>>(new Set());
  const sectionFetchedRef = useRef<Set<string>>(new Set());
  const taskLoadAttemptedRef = useRef<Set<string>>(new Set());

  // Data fetching — only fetch all when user explicitly requests it; default to active-only
  // to stay well under Supabase's 1000-row PostgREST limit
  const { data: projects = [], isLoading } = useQuery<Project[]>({
    queryKey: ["projects", projectStatusFilter],
    queryFn: () => api.getProjects(projectStatusFilter === "all" ? { includeAll: true } : { status: "active" }),
  });

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(),
  });

  const { data: portalActivityRaw = [] } = useQuery<{ project_id: string; read?: boolean }[]>({
    queryKey: ["portal-activity"],
    queryFn: () => fetchWithAuth<{ project_id: string; read?: boolean }[]>("/api/portal-activity"),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  // requires migration 099
  const unreadPortalActivity = useMemo(
    () => portalActivityRaw.filter((i: any) => !i.read),
    [portalActivityRaw]
  );

  const projectPortalMap = useMemo(() => {
    const m: Record<string, number> = {};
    unreadPortalActivity.forEach((i: any) => { m[i.project_id] = (m[i.project_id] ?? 0) + 1; });
    return m;
  }, [unreadPortalActivity]);

  const taskPortalMap = useMemo(() => {
    const m: Record<string, number> = {};
    unreadPortalActivity.forEach((i: any) => { if (i.task_id) m[i.task_id] = (m[i.task_id] ?? 0) + 1; });
    return m;
  }, [unreadPortalActivity]);

  const { data: templates = [] } = useQuery<ProjectTemplate[]>({
    queryKey: ["project-templates"],
    queryFn: () => api.getProjectTemplates().catch(() => []),
  });

  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => api.getTags(),
    staleTime: 60_000,
  });

  const { data: allUsers = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => fetchWithAuth<User[]>("/api/users"),
  });

  const { data: clientGroups = [] } = useQuery<{ id: string; name: string; client_count: number }[]>({
    queryKey: ["client-groups"],
    queryFn: () => fetchWithAuth("/api/client-groups"),
    staleTime: 60_000,
  });

  const activeClientGroupId = useMemo(() => {
    for (const g of filterGroups) {
      for (const r of g.rules) {
        if (r.field === "client_group" && r.values.length > 0) return r.values[0];
      }
    }
    return null;
  }, [filterGroups]);

  const { data: groupMembers = [] } = useQuery<{ account_id: string }[]>({
    queryKey: ["client-group-members", activeClientGroupId],
    queryFn: () => fetchWithAuth(`/api/client-groups/${activeClientGroupId}/members`),
    enabled: !!activeClientGroupId,
  });

  const groupMemberIds = useMemo(
    () => new Set((groupMembers as { account_id: string }[]).map((m) => m.account_id)),
    [groupMembers]
  );

  // Track which account to scroll to after project creation
  const [scrollToAccount, setScrollToAccount] = useState<string | null>(null);

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: { isEdit: boolean; id?: string; payload: object; accountId?: string }) => {
      if (data.isEdit && data.id) {
        return api.updateProject(data.id, data.payload as Partial<Project>);
      }
      return api.createProject(data.payload as Parameters<typeof api.createProject>[0]);
    },
    onSuccess: (result, vars) => {
      const accountId = vars.accountId || result?.account_id;
      const accountsCache = queryClient.getQueryData<Account[]>(["accounts"]) || [];
      const acctName = accountsCache.find((a) => a.id === accountId)?.account_name;
      if (!vars.isEdit) {
        // Immediately inject the new project into the cache so the group appears
        // in the same render as the expand state update — no timing race possible.
        queryClient.setQueryData<Project[]>(["projects", projectStatusFilter], (old = []) => [
          ...old.filter((p) => p.id !== result.id),
          { ...result, account_name: acctName, assigned_users: [], total_tasks: 0, completed_tasks: 0, logged_hours: 0 },
        ]);
        if (accountId) {
          setForceExpandedAccounts((prev) => { const n = new Set(prev); n.add(accountId); return n; });
          setCollapsedClients((prev) => { const n = new Set(prev); n.delete(accountId); return n; });
          setScrollToAccount(accountId);
        }
      } else {
        // Merge updated fields back into the cached project, preserving assigned_users and task counts.
        queryClient.setQueryData<Project[]>(["projects", projectStatusFilter], (old = []) =>
          old.map((p) =>
            p.id === result.id
              ? { ...p, ...result, account_name: acctName || p.account_name }
              : p
          )
        );
      }
      // Do NOT invalidate immediately — background refetch was overwriting the injected data.
      // The query will naturally refetch on next window focus or navigation.
      toast({ title: vars.isEdit ? "Project updated" : "Project created" });
      setModalOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error saving project", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({ title: "Project deleted" });
      setModalOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Error deleting project", description: err.message, variant: "destructive" });
    },
  });

  const addUserMutation = useMutation({
    mutationFn: ({ projectId, userId, role }: { projectId: string; userId: string; role: string }) =>
      api.addProjectUser(projectId, userId, role),
    onSuccess: (added) => {
      setAssignedUsers((prev) => [...prev, added]);
      setNewUserId("");
      setNewUserRole("agent");
      toast({ title: "User added to project" });
    },
    onError: (err: Error) => {
      toast({ title: "Error adding user", description: err.message, variant: "destructive" });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) =>
      api.removeProjectUser(projectId, userId),
    onSuccess: (_, { userId }) => {
      setAssignedUsers((prev) => prev.filter((u) => u.user_id !== userId));
      toast({ title: "User removed from project" });
    },
    onError: (err: Error) => {
      toast({ title: "Error removing user", description: err.message, variant: "destructive" });
    },
  });

  // Workflow pipelines (loaded once, used to resolve stage names + colors)
  const { data: workflowPipelines = [] } = useQuery<WorkflowPipeline[]>({
    queryKey: ["workflow-pipelines"],
    queryFn: () => api.getWorkflowPipelines(),
    staleTime: 5 * 60 * 1000, // rarely changes
  });

  // Track which projects have a pending status mutation (for loading state)
  const [statusPendingIds, setStatusPendingIds] = useState<Set<string>>(new Set());

  const statusMutation = useMutation({
    mutationFn: ({ projectId, statusId }: { projectId: string; statusId: string }) =>
      api.setProjectWorkflowStatus(projectId, statusId),
    onMutate: ({ projectId }) => setStatusPendingIds((s) => new Set(s).add(projectId)),
    onSettled: (_, __, { projectId }) => setStatusPendingIds((s) => { const n = new Set(s); n.delete(projectId); return n; }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
    onError: (err: Error) => toast({ title: "Failed to update status", description: err.message, variant: "destructive" }),
  });

  const addTagMutation = useMutation({
    mutationFn: ({ projectId, tagId }: { projectId: string; tagId: string }) =>
      api.addProjectTag(projectId, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
    onError: (err: Error) => toast({ title: "Failed to add tag", description: err.message, variant: "destructive" }),
  });

  const removeTagMutation = useMutation({
    mutationFn: ({ projectId, tagId }: { projectId: string; tagId: string }) =>
      api.removeProjectTag(projectId, tagId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
    onError: (err: Error) => toast({ title: "Failed to remove tag", description: err.message, variant: "destructive" }),
  });

  const isOverdue = (p: Project) =>
    !!p.due_date && p.status !== "closed" && isBefore(parseISO(p.due_date), startOfDay(new Date()));

  // Filtered projects (client-side)
  function matchesRule(p: Project, rule: FilterRule, userId: string | undefined, memberIds: Set<string>): boolean {
    // Incomplete rule (no values selected yet) = no-op, pass everything through
    if (rule.values.length === 0 && rule.operator !== "is_empty" && rule.operator !== "is_not_empty") return true;

    const today = startOfDay(new Date());
    const weekEnd = endOfWeek(today);
    const nextWeekEnd = addDays(weekEnd, 7);

    switch (rule.field) {
      case "status":
        return rule.operator === "is" ? rule.values.includes(p.status ?? "") : !rule.values.some((v) => v === p.status);

      case "assignee": {
        const taskIds = p.task_assignee_ids ?? [];
        const matched = rule.values.some((v) => {
          if (v === "my_projects") {
            if (!userId) return false;
            return taskIds.length > 0 ? taskIds.includes(userId) : (p.assigned_users?.some((au) => au.user_id === userId) ?? false);
          } else if (v === "unassigned") {
            return !p.assigned_users || p.assigned_users.length === 0;
          } else {
            return taskIds.length > 0 ? taskIds.includes(v) : (p.assigned_users?.some((au) => au.user_id === v) ?? false);
          }
        });
        return rule.operator === "is" ? matched : !matched;
      }

      case "current_assignee": {
        const caMatched = rule.values.some((v) => {
          if (v === "me") return isCurrentAssignee(p, userId);
          if (v === "unassigned") return hasNoCurrentAssignee(p);
          return isCurrentAssignee(p, v);
        });
        return rule.operator === "is" ? caMatched : !caMatched;
      }

      case "client":
        return rule.operator === "is" ? rule.values.includes(p.account_id ?? "") : !rule.values.includes(p.account_id ?? "");

      case "type":
        return rule.operator === "is" ? rule.values.includes(p.project_type ?? "") : !rule.values.includes(p.project_type ?? "");

      case "year":
        return rule.operator === "is" ? rule.values.some((v) => p.tax_year === parseInt(v)) : !rule.values.some((v) => p.tax_year === parseInt(v));

      case "month":
        return rule.operator === "is" ? rule.values.includes(p.period_label ?? "") : !rule.values.includes(p.period_label ?? "");

      case "tag": {
        const hasAnyTag = rule.values.some((v) => (p.project_tags ?? []).some((t: Tag) => t.id === v));
        return rule.operator === "is" ? hasAnyTag : !hasAnyTag;
      }

      case "due_date":
        if (rule.operator === "is_empty") return !p.due_date;
        if (rule.operator === "is_not_empty") return !!p.due_date;
        if (!p.due_date) return false;
        {
          const d = startOfDay(parseISO(p.due_date));
          const matchesDueDate = rule.values.some((v) => {
            switch (v) {
              case "today": return format(d, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");
              case "this_week": return isWithinInterval(d, { start: today, end: weekEnd }) && !isBefore(d, today);
              case "next_week": return isWithinInterval(d, { start: addDays(weekEnd, 1), end: nextWeekEnd });
              case "overdue": return isBefore(d, today);
              default: return false;
            }
          });
          return rule.operator === "is" ? matchesDueDate : !matchesDueDate;
        }

      case "recurring": {
        const isRecurring = !!p.recurring_pattern && p.recurring_pattern !== "none";
        const match = rule.values.some((v) => (v === "recurring" ? isRecurring : !isRecurring));
        return rule.operator === "is" ? match : !match;
      }

      case "client_group":
        return memberIds.has(p.account_id ?? "");

      default:
        return true;
    }
  }

  const displayedProjects = useMemo(() => {
    let list = projects;

    if (projectStatusFilter === "active") {
      list = list.filter((p) => p.status !== "closed" && p.status !== "completed");
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => (p.title ?? "").toLowerCase().includes(q));
    }

    if (hideFuture) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() + 7);
      const cutoffDate = cutoff.toISOString().slice(0, 10);
      list = list.filter((p) => !p.start_date || p.start_date <= cutoffDate);
    }

    const activeGroups = filterGroups.filter((g) => g.rules.length > 0);
    if (activeGroups.length > 0) {
      list = list.filter((p) =>
        // Project passes if it matches AT LEAST ONE group (groups are OR'd together)
        activeGroups.some((group) => {
          if (group.combinator === "AND") {
            return group.rules.every((rule) => matchesRule(p, rule, user?.id, groupMemberIds));
          } else {
            return group.rules.some((rule) => matchesRule(p, rule, user?.id, groupMemberIds));
          }
        })
      );
    }

    if (myWorkOnly && user?.id) {
      // ENG-117: anyone on the current step (shared tasks, unordered sections), not one pick.
      list = list.filter((p) => isCurrentAssignee(p, user.id));
    }

    if (focusProjectId) {
      list = list.filter((p) => p.id === focusProjectId);
      // If the focused project was filtered out (e.g. closed while in Active Projects mode),
      // use the individually-fetched override (handles closed jobs not in the active list)
      if (list.length === 0) {
        if (focusedProjectOverride) list = [focusedProjectOverride];
        else {
          const focused = projects.find((p) => p.id === focusProjectId);
          if (focused) list = [focused];
        }
      }
    }

    return list;
  }, [projects, projectStatusFilter, searchQuery, hideFuture, filterGroups, groupMemberIds, user?.id, focusProjectId, focusedProjectOverride, myWorkOnly]);

  // Summary stats — computed from displayedProjects so counts respect My Work and active filters
  const summaryStats = useMemo(() => {
    const today = startOfDay(new Date());
    const weekEnd = endOfWeek(today);
    const nextWeekEnd = addDays(weekEnd, 7);
    let dueToday = 0, dueThisWeek = 0, dueNextWeek = 0, overdue = 0;
    displayedProjects.forEach((p) => {
      if (p.status === "closed" || !p.due_date) return;
      const d = startOfDay(parseISO(p.due_date));
      if (isBefore(d, today)) overdue++;
      else if (format(d, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")) dueToday++;
      else if (isWithinInterval(d, { start: today, end: weekEnd })) dueThisWeek++;
      else if (isWithinInterval(d, { start: addDays(weekEnd, 1), end: nextWeekEnd })) dueNextWeek++;
    });
    return { dueToday, dueThisWeek, dueNextWeek, overdue };
  }, [displayedProjects]);

  // Auto-expand focused project and load its tasks (including closed projects not in active list)
  useEffect(() => {
    if (!focusProjectId) { setFocusedProjectOverride(null); return; }
    // Idempotent: return the SAME Set ref when already expanded so React bails out.
    // Otherwise this fires a new Set every run, and because `projects` defaults to a
    // fresh `[]` while the query loads (a dep of this effect), it re-runs every render
    // → infinite update loop (React #185). (B3 hotfix.)
    setExpandedProjects(prev => prev.has(focusProjectId) ? prev : new Set([...prev, focusProjectId]));
    // If project isn't in the loaded list (e.g. closed while viewing Active Projects), fetch it individually.
    // Only clear the override when the project IS found in the list — don't clear it on every render
    // (that would cause an infinite loop since setting state re-fires the effect).
    const inList = projects.find((p) => p.id === focusProjectId);
    if (inList) {
      setFocusedProjectOverride(null);
    } else if (projects.length > 0) {
      api.getProject(focusProjectId).then((p) => setFocusedProjectOverride(p)).catch(() => {});
    }
    if (projects.length === 0 || projectInlineTasks.has(focusProjectId)) return;
    setInlineTaskLoading(prev => new Set(prev).add(focusProjectId));
    Promise.all([api.getProjectTasks(focusProjectId), api.getProjectSections(focusProjectId)])
      .then(([tasks, sections]) => {
        setProjectInlineTasks(prev => new Map(prev).set(focusProjectId, tasks));
        setProjectSectionsMap(prev => new Map(prev).set(focusProjectId, sections));
        const done = tasks.filter((t: any) => t.status === "completed" || t.status === "done").length;
        setTaskProgressMap(prev => new Map(prev).set(focusProjectId, { total: tasks.length, done }));
        fetchedProjectsRef.current.add(focusProjectId);
      })
      .catch(() => {})
      .finally(() => setInlineTaskLoading(prev => { const n = new Set(prev); n.delete(focusProjectId); return n; }));
  }, [focusProjectId, projects]); // intentionally excludes focusedProjectOverride — it's set by this effect, not consumed by it

  // Expose filtered project IDs to CentCom AI
  const { setFilteredProjectIds } = useAIPageContext();
  useEffect(() => {
    setFilteredProjectIds(displayedProjects.map(p => p.id));
    return () => setFilteredProjectIds([]);
  }, [displayedProjects, setFilteredProjectIds]);

  // Batch-fetch sections for all visible projects so project-row header buttons can show.
  // for list view only
  useEffect(() => {
    if (viewMode !== "list") return;
    const unfetched = displayedProjects.filter(p => !sectionFetchedRef.current.has(p.id));
    if (unfetched.length === 0) return;
    unfetched.forEach(p => sectionFetchedRef.current.add(p.id));
    Promise.allSettled(unfetched.map(p =>
      api.getProjectSections(p.id).then(sections => {
        setProjectSectionsMap(prev => new Map(prev).set(p.id, sections));
        const cs = sections.find((s: TaskSection) => s.section_type === "client_tasks");
        if (cs) {
          api.getSectionReminder(cs.id).then(cfg => {
            if (cfg) setSectionReminderMap(m => ({ ...m, [cs.id]: { is_active: cfg.is_active, days: cfg.days, schedule_type: cfg.schedule_type ?? "interval", monthly_day: cfg.monthly_day } }));
          }).catch(() => {});
        }
      })
    ));
  }, [displayedProjects, viewMode]);

  // Shared opener for the Auto-Reminders modal (project-header + per-section buttons).
  // Mirrors the AccountDetail panel: loads the saved config AND the account's contacts,
  // prefilling "Send to" with the contacts on file when nothing is saved yet.
  const openReminderModal = (sectionId: string, accountId: string, projectId: string, projectStatus: string, projectDeletedAt: string | null) => {
    const existing = sectionReminderMap[sectionId];
    setReminderConfig({
      is_active: existing?.is_active ?? false,
      days: existing?.days ?? 3,
      contacts: "",
      schedule_type: (existing?.schedule_type as "interval" | "monthly" | "section") ?? "interval",
      monthly_day: existing?.monthly_day ?? 1,
      trigger_section_id: "",
    });
    setReminderSavedCfg(null);
    setReminderModal({ sectionId, accountId, projectId, projectStatus, projectDeletedAt });
    setReminderLoading(true);
    Promise.all([
      api.getSectionReminder(sectionId).then(cfg => ({ ok: true as const, cfg })).catch(() => ({ ok: false as const, cfg: null })),
      api.getContacts({ accountId }).catch(() => []),
    ]).then(([reminderRes, contacts]) => {
      setReminderLoading(false);
      if (!reminderRes.ok) {
        // Don't seed the draft as "never configured" on a failed read — a save from that
        // state would wipe live config. Keep the map-seeded draft and warn.
        toast({ title: "Couldn't load saved reminder settings", description: "Close and reopen to retry before saving.", variant: "destructive" });
        return;
      }
      const cfg = reminderRes.cfg;
      const defaultContacts = (contacts ?? []).map(c => c.email).filter((e): e is string => !!e).join(", ");
      setReminderSavedCfg(cfg);
      setReminderConfig({
        is_active: cfg?.is_active ?? false,
        days: cfg?.days ?? 3,
        contacts: (cfg?.contacts ?? []).length ? cfg!.contacts.join(", ") : defaultContacts,
        schedule_type: (cfg?.schedule_type as "interval" | "monthly" | "section") ?? "interval",
        monthly_day: cfg?.monthly_day ?? 1,
        trigger_section_id: cfg?.trigger_section_id ?? "",
      });
    });
  };

  const textCmp = (a: string, b: string) => TEXT_COLLATOR.compare(a, b);
  // "which client is this project on"
  const clientNameOf = (p: Project) => p.account_name || p.accounts?.account_name || "";

  const applyProjectSort = (rules: { field: SortField; dir: "asc" | "desc" }[], a: Project, b: Project): number => {
    for (const rule of rules) {
      let cmp = 0;
      if (rule.field === "due_date") {
        const da = a.due_date ? new Date(a.due_date).getTime() : (rule.dir === "asc" ? Infinity : -Infinity);
        const db = b.due_date ? new Date(b.due_date).getTime() : (rule.dir === "asc" ? Infinity : -Infinity);
        cmp = da - db;
      } else if (rule.field === "start_date") {
        const da = a.start_date ? new Date(a.start_date).getTime() : (rule.dir === "asc" ? Infinity : -Infinity);
        const db = b.start_date ? new Date(b.start_date).getTime() : (rule.dir === "asc" ? Infinity : -Infinity);
        cmp = da - db;
      } else if (rule.field === "client") {
        cmp = textCmp(clientNameOf(a), clientNameOf(b));
      } else if (rule.field === "title") {
        cmp = textCmp(a.title ?? "", b.title ?? "");
      } else if (rule.field === "type") {
        const ta = a.project_type ?? "￿"; const tb = b.project_type ?? "￿";
        cmp = ta.localeCompare(tb);
      } else if (rule.field === "year") {
        const ya = a.tax_year ?? (rule.dir === "asc" ? Infinity : -Infinity);
        const yb = b.tax_year ?? (rule.dir === "asc" ? Infinity : -Infinity);
        cmp = (ya as number) - (yb as number);
      } else if (rule.field === "status") {
        cmp = STATUS_ORDER.indexOf(a.status ?? "open") - STATUS_ORDER.indexOf(b.status ?? "open");
      }
      if (cmp !== 0) return rule.dir === "asc" ? cmp : -cmp;
    }
    return 0;
  };

  // Group by client
  const groupedProjects = useMemo(() => {
    const map = new Map<string, { account_id: string; account_name: string; projects: Project[] }>();
    for (const p of displayedProjects) {
      const accId = p.account_id ?? "unknown";
      const accName = p.account_name || p.accounts?.account_name || "Unknown Client";
      if (!map.has(accId)) map.set(accId, { account_id: accId, account_name: accName, projects: [] });
      map.get(accId)!.projects.push(p);
    }
    return Array.from(map.values())
      .sort((a, b) => textCmp(a.account_name, b.account_name))
      .map((c) => ({ ...c, projects: c.projects.slice().sort((a, b) => applyProjectSort(sortRules, a, b)) }));
  }, [displayedProjects, sortRules]);

  const groupedByAlt = useMemo(() => {
    if (groupBy === "client") return [] as { key: string; label: string; projects: Project[] }[];
    const map = new Map<string, { key: string; label: string; projects: Project[] }>();

    for (const p of displayedProjects) {
      let entries: { key: string; label: string }[] = [];

      if (groupBy === "status") {
        const k = p.status ?? "open";
        entries = [{ key: k, label: statusLabels[k] ?? k }];
      } else if (groupBy === "type") {
        const k = p.project_type ?? "__none__";
        entries = [{ key: k, label: p.project_type ?? "No Type" }];
      } else if (groupBy === "assignee") {
        const ids = p.task_assignee_ids ?? [];
        if (ids.length === 0) {
          entries = [{ key: "__unassigned__", label: "Unassigned" }];
        } else {
          const u = allUsers.find((x) => x.id === ids[0]);
          entries = [{ key: ids[0], label: u?.name ?? u?.email ?? "Unknown" }];
        }
      } else if (groupBy === "current_assignee") {
        const cid = p.current_assignee_id;
        if (!cid) {
          entries = [{ key: "__unassigned__", label: "Unassigned" }];
        } else {
          const u = allUsers.find((x) => x.id === cid);
          entries = [{ key: cid, label: u?.name ?? u?.email ?? "Unknown" }];
        }
      } else if (groupBy === "year") {
        const k = p.tax_year != null ? String(p.tax_year) : "__none__";
        entries = [{ key: k, label: p.tax_year != null ? String(p.tax_year) : "No Year" }];
      } else {
        // period
        const k = p.period_label ?? "__none__";
        entries = [{ key: k, label: p.period_label ? (PERIOD_NAMES[p.period_label] ?? p.period_label) : "No Period" }];
      }

      for (const { key, label } of entries) {
        if (!map.has(key)) map.set(key, { key, label, projects: [] });
        map.get(key)!.projects.push(p);
      }
    }

    const groups = Array.from(map.values());

    if (groupBy === "status") {
      groups.sort((a, b) => STATUS_ORDER.indexOf(a.key) - STATUS_ORDER.indexOf(b.key));
    } else if (groupBy === "year") {
      groups.sort((a, b) => {
        if (a.key === "__none__") return 1;
        if (b.key === "__none__") return -1;
        return parseInt(b.key) - parseInt(a.key);
      });
    } else if (groupBy === "period") {
      groups.sort((a, b) => {
        if (a.key === "__none__") return 1;
        if (b.key === "__none__") return -1;
        return a.key.localeCompare(b.key);
      });
    } else {
      groups.sort((a, b) => {
        if (a.key === "__none__" || a.key === "__unassigned__") return 1;
        if (b.key === "__none__" || b.key === "__unassigned__") return -1;
        return a.label.localeCompare(b.label);
      });
    }

    groups.forEach((g) => g.projects.sort((a, b) => applyProjectSort(sortRules, a, b)));

    return groups;
  }, [groupBy, displayedProjects, allUsers, sortRules]);

  const groupedByCompound = useMemo(() => {
    if (groupBy2 === "none" || groupBy === "client") return [] as { key: string; label: string; subgroups: { key: string; label: string; projects: Project[] }[] }[];

    const getInnerEntries = (p: Project): { key: string; label: string }[] => {
      if (groupBy2 === "status") {
        const k = p.status ?? "open";
        return [{ key: k, label: statusLabels[k] ?? k }];
      } else if (groupBy2 === "type") {
        const k = p.project_type ?? "__none__";
        return [{ key: k, label: p.project_type ?? "No Type" }];
      } else if (groupBy2 === "assignee") {
        const ids = p.task_assignee_ids ?? [];
        if (ids.length === 0) return [{ key: "__unassigned__", label: "Unassigned" }];
        const u = allUsers.find((x) => x.id === ids[0]);
        return [{ key: ids[0], label: u?.name ?? u?.email ?? "Unknown" }];
      } else if (groupBy2 === "current_assignee") {
        const cid = p.current_assignee_id;
        if (!cid) return [{ key: "__unassigned__", label: "Unassigned" }];
        const u = allUsers.find((x) => x.id === cid);
        return [{ key: cid, label: u?.name ?? u?.email ?? "Unknown" }];
      } else if (groupBy2 === "year") {
        const k = p.tax_year != null ? String(p.tax_year) : "__none__";
        return [{ key: k, label: p.tax_year != null ? String(p.tax_year) : "No Year" }];
      } else {
        const k = p.period_label ?? "__none__";
        return [{ key: k, label: p.period_label ? (PERIOD_NAMES[p.period_label] ?? p.period_label) : "No Period" }];
      }
    };

    return groupedByAlt.map((outer) => {
      const innerMap = new Map<string, { key: string; label: string; projects: Project[] }>();
      for (const p of outer.projects) {
        for (const { key, label } of getInnerEntries(p)) {
          if (!innerMap.has(key)) innerMap.set(key, { key, label, projects: [] });
          innerMap.get(key)!.projects.push(p);
        }
      }
      const subgroups = Array.from(innerMap.values());
      if (groupBy2 === "status") {
        subgroups.sort((a, b) => STATUS_ORDER.indexOf(a.key) - STATUS_ORDER.indexOf(b.key));
      } else if (groupBy2 === "year") {
        subgroups.sort((a, b) => {
          if (a.key === "__none__") return 1;
          if (b.key === "__none__") return -1;
          return parseInt(b.key) - parseInt(a.key);
        });
      } else if (groupBy2 === "period") {
        subgroups.sort((a, b) => {
          if (a.key === "__none__") return 1;
          if (b.key === "__none__") return -1;
          return a.key.localeCompare(b.key);
        });
      } else {
        subgroups.sort((a, b) => {
          if (a.key === "__none__" || a.key === "__unassigned__") return 1;
          if (b.key === "__none__" || b.key === "__unassigned__") return -1;
          return a.label.localeCompare(b.label);
        });
      }
      subgroups.forEach((sg) => sg.projects.sort((a, b) => applyProjectSort(sortRules, a, b)));
      return { key: outer.key, label: outer.label, subgroups };
    });
  }, [groupBy2, groupBy, groupedByAlt, allUsers, sortRules]);

  // --- Table view (flat / optionally sectioned) ---
  const TEXT_COLS: SortableColumn[] = ["client", "project"];
  const colValue = (p: Project, col: SortableColumn): string => {
    switch (col) {
      case "client": return clientNameOf(p);
      case "project": return p.title ?? "";
      case "start_date": return p.start_date ?? "";
      case "internal_due_date": return p.internal_due_date ?? "";
      case "due_date": return p.due_date ?? "";
      case "next_task_due_date": return p.next_task_due_date ?? "";
      default: return "";
    }
  };
  const sortByCol = (list: Project[]): Project[] => {
    const { col, dir } = colSort;
    if (!col) return list;
    const isText = TEXT_COLS.includes(col);
    return [...list].sort((a, b) => {
      const av = colValue(a, col), bv = colValue(b, col);
      const cmp = isText ? textCmp(av, bv) : av < bv ? -1 : av > bv ? 1 : 0;
      return dir === "asc" ? cmp : -cmp;
    });
  };
  // Flat list for the table: column-header sort if active, else the multi-level Sort panel order.
  const tableProjects = useMemo(() => {
    if (colSort.col) return sortByCol(displayedProjects);
    const sorted = [...displayedProjects].sort((a, b) => applyProjectSort(sortRules, a, b));
    if (groupBy !== "client") return sorted;
    // cluster alphabetically
    return sorted.sort((a, b) => textCmp(clientNameOf(a), clientNameOf(b)));
  }, [displayedProjects, colSort, sortRules, groupBy]);
  // Optional sectioning when a non-client Group By is chosen. "By Client" renders flat — client is a column,
  // and the rich by-client grouped experience lives in the List (accordion) view.
  const tableGroups = useMemo<ProjectsTableGroup[] | undefined>(() => {
    if (groupBy === "client") return undefined;
    return groupedByAlt.map((g) => ({
      key: g.key,
      label: g.label,
      projects: colSort.col ? sortByCol(g.projects) : g.projects,
      openCount: g.projects.filter((p) => p.status !== "closed" && p.status !== "completed").length,
      totalCount: g.projects.length,
    }));
  }, [groupBy, groupedByAlt, colSort]);

  // Render-prop helpers reused by ProjectsTable (keep it a pure layout component).
  // must stay referentially stable due to memoization
  const usersById = useMemo(() => new Map(allUsers.map((u) => [u.id, u])), [allUsers]);
  const removeTag = removeTagMutation.mutate;
  const setStatus = statusMutation.mutate;

  // Table shows only the CURRENT assignee (the person whose step is next), as a single avatar.
  const renderAssigneeAvatars = useCallback((p: Project) => {
    const uid = p.current_assignee_id;
    if (!uid) return <span className="text-xs text-muted-foreground">—</span>;
    const u = usersById.get(uid);
    if (!u?.name && !u?.email) return <span className="text-xs text-muted-foreground">—</span>;
    return <UserAvatar name={u?.name ?? null} email={u?.email ?? null} />;
  }, [usersById]);
  const renderProjectTags = useCallback((p: Project) => (
    <>
      {(p.project_tags ?? []).map((tag: Tag) => (
        <TagChip key={tag.id} tag={tag} onRemove={() => removeTag({ projectId: p.id, tagId: tag.id })} />
      ))}
    </>
  ), [removeTag]);
  const renderWorkflowBadge = useCallback((p: Project) =>
    workflowPipelines.length > 0 ? (
      <WorkflowStatusBadge
        project={p}
        pipelines={workflowPipelines}
        onStatusChange={(pid, sid) => setStatus({ projectId: pid, statusId: sid })}
        isPending={statusPendingIds.has(p.id)}
      />
    ) : null,
  [workflowPipelines, setStatus, statusPendingIds]);
  const handleTableOpenProject = useCallback(
    (p: Project) => setLocation(`/accounts/${p.account_id}?openProject=${p.id}`), [setLocation]);
  const handleTableOpenChat = useCallback(
    (p: Project) => setMessagesProject({ id: p.id, title: p.title }), []);
  const handleTableToggleGroup = useCallback((key: string) => {
    setExpandedGroupsAlt((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);

  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [projectInlineTasks, setProjectInlineTasks] = useState<Map<string, any[]>>(new Map());
  const [inlineTaskLoading, setInlineTaskLoading] = useState<Set<string>>(new Set());
  const [projectSectionsMap, setProjectSectionsMap] = useState<Map<string, TaskSection[]>>(new Map());

  // Auto-load tasks for any expanded project whose sections are pre-fetched but tasks haven't loaded.
  // Recovers from the focusProjectId effect's silent-failure path.
  useEffect(() => {
    for (const projectId of expandedProjects) {
      if (
        projectInlineTasks.has(projectId) ||
        inlineTaskLoading.has(projectId) ||
        taskLoadAttemptedRef.current.has(projectId) ||
        (projectSectionsMap.get(projectId)?.length ?? 0) === 0
      ) continue;
      taskLoadAttemptedRef.current.add(projectId);
      setInlineTaskLoading(prev => new Set(prev).add(projectId));
      Promise.all([api.getProjectTasks(projectId), api.getProjectSections(projectId)])
        .then(([tasks, sections]) => {
          setProjectInlineTasks(prev => new Map(prev).set(projectId, tasks));
          setProjectSectionsMap(prev => new Map(prev).set(projectId, sections));
          const done = tasks.filter((t: any) => t.status === "completed" || t.status === "done").length;
          setTaskProgressMap(prev => new Map(prev).set(projectId, { total: tasks.length, done }));
          fetchedProjectsRef.current.add(projectId);
        })
        .catch(err => { console.error("Auto task load failed for project", projectId, err); })
        .finally(() => setInlineTaskLoading(prev => { const n = new Set(prev); n.delete(projectId); return n; }));
    }
  }, [expandedProjects, projectSectionsMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inline "Add task" state — tracks which project/section has the add-task input open
  const [addingTaskForProject, setAddingTaskForProject] = useState<string | null>(null);
  const [addingTaskForSection, setAddingTaskForSection] = useState<string | null>(null); // sectionId or "__none__"
  const [newTaskTitle, setNewTaskTitle] = useState("");

  const [confirmCompleteProject, setConfirmCompleteProject] = useState<
    { id: string; title: string; x: number; y: number; lines: string[] } | null
  >(null);

  const completeProjectConfirmed = async (target: { id: string; x: number; y: number }) => {
    // The server completes the project's open tasks (root AND sub-tasks) and triggers the
    // recurrence spawn — see the close cascade in PUT /api/projects/:id. This used to be done
    // here, but it could only ever complete tasks this page had already LOADED, and it filtered
    // out sub-tasks, so closing a collapsed project completed nothing. Doing it server-side also
    // means the account-page checkbox behaves identically.
    saveMutation.mutate({ isEdit: true, id: target.id, payload: { status: "closed" } });
    triggerProjectConfetti(target.x, target.y);
    // Optimistic: reflect the completion locally for whatever this page has loaded.
    setProjectInlineTasks((prev) => {
      const loaded = prev.get(target.id);
      if (!loaded || loaded.length === 0) return prev;
      return new Map(prev).set(target.id, loaded.map((t: any) => ({ ...t, status: "done" })));
    });
  };

  // Section management state
  const [sectionRulesOpen, setSectionRulesOpen] = useState<{ sectionId: string; accountId: string | null } | null>(null);
  const [notifyModal, setNotifyModal] = useState<{ projectId: string; projectTitle: string; accountId: string } | null>(null);
  const [reminderModal, setReminderModal] = useState<{ sectionId: string; accountId: string; projectId: string; projectStatus: string; projectDeletedAt: string | null } | null>(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderConfig, setReminderConfig] = useState<{ is_active: boolean; days: number; contacts: string; schedule_type: "interval" | "monthly" | "section"; monthly_day: number; trigger_section_id: string } | null>(null);
  const [reminderSavedCfg, setReminderSavedCfg] = useState<SectionReminderConfig | null>(null);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [sectionReminderMap, setSectionReminderMap] = useState<Record<string, { is_active: boolean; days: number; schedule_type: string; monthly_day?: number | null } | null>>({});
  const [renamingSection, setRenamingSection] = useState<{ sectionId: string; name: string; projectId: string } | null>(null);
  const [addingSection, setAddingSection] = useState<string | null>(null); // projectId
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionIsClient, setNewSectionIsClient] = useState(false);
  const [newSectionIsSequential, setNewSectionIsSequential] = useState(false);
  // Collapsed sections: Set of "projectId:sectionId" keys
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const toggleProjectSection = (projectId: string, sectionId: string) => {
    const key = `${projectId}:${sectionId}`;
    setCollapsedSections(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  // ENG-86: auto-collapse a section on load when every one of its root tasks is
  // already done — one shot per project, tracked in a ref so a later task refetch
  // (e.g. completing the last open task) can never live-collapse a section out from
  // under the user. Only fires once a project's sections AND tasks have both landed
  // in projectSectionsMap/projectInlineTasks (fed by three separate effects above:
  // the focusProjectId deep-link fetch, the recovery effect, and manual expand).
  const autoCollapseSeededRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const [projectId, tasks] of projectInlineTasks) {
      if (autoCollapseSeededRef.current.has(projectId)) continue;
      const sections = projectSectionsMap.get(projectId);
      if (!sections) continue; // wait for sections to land before seeding
      autoCollapseSeededRef.current.add(projectId);

      const bucket = new Map<string, any[]>();
      for (const task of tasks) {
        const sectionId = (task as any).section_id || "__none__";
        const list = bucket.get(sectionId);
        if (list) list.push(task);
        else bucket.set(sectionId, [task]);
      }
      // No exemptTaskId here — Projects.tsx only ever deep-links at the project
      // level (?openProject / focusProjectId); task-level ?openTask deep-links
      // (bell/email/"Your turn") land on AccountDetail, not here.
      const toCollapse = computeAutoCollapsedSectionIds(sections, bucket);
      if (toCollapse.size === 0) continue;
      setCollapsedSections(prev => {
        const n = new Set(prev);
        for (const sectionId of toCollapse) n.add(`${projectId}:${sectionId}`);
        return n;
      });
    }
  }, [projectInlineTasks, projectSectionsMap]);
  const [dragSectionId, setDragSectionId] = useState<string | null>(null);
  const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(null);

  // Drag-and-drop task reordering state
  const [dragTaskId, setDragTaskId] = useState<string | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<string | null>(null);
  const [dragProjectId, setDragProjectId] = useState<string | null>(null);

  // Collapse state — default all collapsed; when arriving from AccountDetail, keep that account expanded
  const [collapsedClients, setCollapsedClients] = useState<Set<string>>(new Set());
  // forceExpandedAccounts overrides collapsedClients — used after project creation
  // so re-initialization of collapsedClients can't hide the newly created project's group
  const [forceExpandedAccounts, setForceExpandedAccounts] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && groupedProjects.length > 0) {
      initializedRef.current = true;
      setCollapsedClients(new Set(groupedProjects.map((c) => c.account_id)));
    }
  }, [groupedProjects]);

  // After creating a project, scroll to the newly expanded client group
  useEffect(() => {
    if (!scrollToAccount) return;
    const el = document.querySelector(`[data-account-id="${scrollToAccount}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollToAccount(null);
    }
  }, [scrollToAccount, groupedProjects]);

  // Deep-link: /projects?edit=<id> — auto-open edit modal (e.g. from AccountDetail pencil button)
  useEffect(() => {
    if (projects.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (!editId) return;
    const target = projects.find((p) => p.id === editId);
    if (target) {
      openEditModal(target);
      // Clean the param from the URL without triggering a navigation
      const clean = window.location.pathname;
      window.history.replaceState(null, "", clean);
    }
  }, [projects]);

  const toggleClient = (id: string) => {
    // If user manually toggles a force-expanded group, remove it from force-expanded
    setForceExpandedAccounts((prev) => { const n = new Set(prev); n.delete(id); return n; });
    setCollapsedClients((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // Fetch task progress when a client group is expanded
  const fetchTaskProgress = (clientProjects: Project[]) => {
    clientProjects.forEach(async (p) => {
      if (fetchedProjectsRef.current.has(p.id)) return;
      fetchedProjectsRef.current.add(p.id);
      try {
        const tasks = await api.getProjectTasks(p.id);
        const done = tasks.filter((t) => t.status === "completed" || t.status === "done").length;
        setTaskProgressMap((prev) => new Map(prev).set(p.id, { total: tasks.length, done }));
      } catch {
        // silently ignore
      }
    });
  };

  const handleToggleClient = (accountId: string, clientProjects: Project[]) => {
    const wasCollapsed = collapsedClients.has(accountId);
    toggleClient(accountId);
    if (wasCollapsed) fetchTaskProgress(clientProjects);
  };

  const handleToggleProjectTasks = async (projectId: string) => {
    const isExpanded = expandedProjects.has(projectId);
    setExpandedProjects((prev) => { const n = new Set(prev); isExpanded ? n.delete(projectId) : n.add(projectId); return n; });
    if (!isExpanded && !projectInlineTasks.has(projectId)) {
      setInlineTaskLoading((prev) => new Set(prev).add(projectId));
      try {
        const [tasks, sections] = await Promise.all([
          api.getProjectTasks(projectId),
          api.getProjectSections(projectId),
        ]);
        setProjectInlineTasks((prev) => new Map(prev).set(projectId, tasks));
        setProjectSectionsMap((prev) => new Map(prev).set(projectId, sections));
        const done = tasks.filter((t: any) => t.status === "completed" || t.status === "done").length;
        setTaskProgressMap((prev) => new Map(prev).set(projectId, { total: tasks.length, done }));
        fetchedProjectsRef.current.add(projectId);
      } catch {
        // silently ignore
      } finally {
        setInlineTaskLoading((prev) => { const n = new Set(prev); n.delete(projectId); return n; });
      }
    }
  };

  const handleTaskStatusChange = async (projectId: string, taskId: string, done: boolean) => {
    try {
      // Note: the actual API call is already made inside TaskDetailRow.toggleStatus
      // We just update local state and check whether the project can be closed.
      // The decision is computed OUT here rather than inside the state updater —
      // an updater must stay pure, and it now opens a dialog rather than firing a
      // mutation, which a StrictMode double-invoke would ask twice.
      const applyStatus = (list: any[]) =>
        list.map((t: any) => (t.id === taskId ? { ...t, status: done ? "done" : "open" } : t));
      const isDone = (t: any) => t.status === "done" || t.status === "completed";
      const updated = applyStatus(projectInlineTasks.get(projectId) || []);
      const doneCount = updated.filter(isDone).length;
      const total = updated.filter((t: any) => !t.parent_task_id).length; // only root tasks

      setProjectInlineTasks((prev) => new Map(prev).set(projectId, applyStatus(prev.get(projectId) || [])));
      setTaskProgressMap((pm) => new Map(pm).set(projectId, { total: updated.length, done: doneCount }));

      // Completing the last task used to close the project on the spot. Closing runs
      // the server-side cascade over every remaining task AND creates the next
      // occurrence of a recurring project, so it asks first — same dialog as the
      // project checkbox. Declining leaves the project open; the task stays done.
      if (done && total > 0 && doneCount >= total) {
        const project = projects.find((p) => p.id === projectId);
        const stillOpen = updated.filter((t: any) => !isDone(t));
        setConfirmCompleteProject({
          id: projectId,
          title: project?.title ?? "",
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
          // The cached aggregates still count the task just completed as open, so
          // the counts are passed explicitly from what this page is showing.
          lines: [
            "That was the last open task on it.",
            ...buildProjectCloseWarning({
              ...(project ?? {}),
              openTaskCount: stillOpen.length,
              openClientTaskCount: stillOpen.filter((t: any) => t.is_client_task).length,
            }).lines,
          ],
        });
      }
    } catch {
      toast({ title: "Failed to update task", variant: "destructive" });
    }
  };

  const handleTaskUpdate = (projectId: string, taskId: string, updates: Partial<ProjectTask>) => {
    setProjectInlineTasks((prev) => {
      const tasks = prev.get(projectId) || [];
      return new Map(prev).set(projectId, tasks.map((t: any) =>
        t.id === taskId ? { ...t, ...updates } : t
      ));
    });
  };

  const handleTaskDelete = (projectId: string, taskId: string) => {
    setProjectInlineTasks((prev) => {
      const tasks = prev.get(projectId) || [];
      const updated = tasks.filter((t: any) => t.id !== taskId);
      const doneCount = updated.filter((t: any) => t.status === "done" || t.status === "completed").length;
      setTaskProgressMap((pm) => new Map(pm).set(projectId, { total: updated.length, done: doneCount }));
      return new Map(prev).set(projectId, updated);
    });
  };

  const handleAddTask = async (projectId: string, sectionId?: string | null) => {
    const title = newTaskTitle.trim();
    if (!title) { setAddingTaskForProject(null); setAddingTaskForSection(null); setNewTaskTitle(""); return; }
    // Clear state before the async call to prevent double-submission on fast Enter
    setNewTaskTitle("");
    setAddingTaskForProject(null);
    setAddingTaskForSection(null);
    try {
      const newTask = await api.createProjectTask(projectId, { title, section_id: sectionId ?? undefined });
      setProjectInlineTasks((prev) => {
        const tasks = prev.get(projectId) || [];
        const updated = [...tasks, newTask];
        setTaskProgressMap((pm) => new Map(pm).set(projectId, { total: updated.length, done: updated.filter((t: any) => t.status === "done" || t.status === "completed").length }));
        return new Map(prev).set(projectId, updated);
      });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // Drag-and-drop handlers for task reordering
  const handleTaskDragStart = (e: React.DragEvent, taskId: string, projectId: string) => {
    setDragTaskId(taskId);
    setDragProjectId(projectId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleTaskDragOver = (e: React.DragEvent, taskId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (taskId !== dragTaskId) setDragOverTaskId(taskId);
  };

  // Moves a root task within its section or into another one (drop on a task, or on a
  // section header / empty section body). Only the two sections involved are renumbered;
  // the server cascades a section change to the task's sub-tasks.
  const applyTaskMove = (projectId: string, target: MoveTarget) => {
    const dragId = dragTaskId;
    const sameProject = dragProjectId === projectId;
    setDragTaskId(null); setDragOverTaskId(null); setDragProjectId(null); setDragOverSectionId(null);
    if (!dragId || !sameProject) return;
    const current = projectInlineTasks.get(projectId) || [];
    const result = moveTask(current as any[], dragId, target);
    if (!result) return;
    const sections = projectSectionsMap.get(projectId) || [];
    const nextTasks = result.tasks.map((t: any) => {
      const change = result.changes.find(c => c.id === t.id && c.section_id !== undefined);
      if (!change) return t;
      return { ...t, task_sections: sections.find(s => s.id === change.section_id) ?? null };
    });
    setProjectInlineTasks(prev => new Map(prev).set(projectId, nextTasks));
    Promise.all(result.changes.map(c => {
      const body: Record<string, unknown> = { list_index: c.list_index };
      if (c.section_id !== undefined) body.section_id = c.section_id;
      return fetchWithAuth(`/api/project-tasks/${c.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    })).catch(() => {
      toast({ title: "Couldn't save the task move", description: "Refresh to see the saved order.", variant: "destructive" });
    });
  };

  const handleTaskDrop = (e: React.DragEvent, dropOnTaskId: string, projectId: string) => {
    e.preventDefault();
    applyTaskMove(projectId, { taskId: dropOnTaskId });
  };

  const handleTaskDragEnd = () => {
    setDragTaskId(null); setDragOverTaskId(null); setDragProjectId(null); setDragOverSectionId(null);
  };

  // ─── Section management handlers ────────────────────────────────────────────

  const handleRenameSection = async (sectionId: string, name: string, projectId: string) => {
    const trimmed = name.trim();
    if (!trimmed) { setRenamingSection(null); return; }
    try {
      await api.updateTaskSection(sectionId, { name: trimmed });
      setProjectSectionsMap((prev) => {
        const secs = (prev.get(projectId) || []).map((s) => s.id === sectionId ? { ...s, name: trimmed } : s);
        return new Map(prev).set(projectId, secs);
      });
    } catch {
      toast({ title: "Failed to rename section", variant: "destructive" });
    }
    setRenamingSection(null);
  };

  const handleDeleteSection = async (sectionId: string, projectId: string) => {
    try {
      await api.deleteTaskSection(sectionId);
      setProjectSectionsMap((prev) => {
        const secs = (prev.get(projectId) || []).filter((s) => s.id !== sectionId);
        return new Map(prev).set(projectId, secs);
      });
      // Tasks become unsectioned at DB level (ON DELETE SET NULL); update local state too
      setProjectInlineTasks((prev) => {
        const tasks = (prev.get(projectId) || []).map((t: any) =>
          t.section_id === sectionId ? { ...t, section_id: null, task_sections: null } : t
        );
        return new Map(prev).set(projectId, tasks);
      });
    } catch {
      toast({ title: "Failed to delete section", variant: "destructive" });
    }
  };

  const handleAddSection = async (projectId: string) => {
    const trimmed = newSectionName.trim();
    const isClient = newSectionIsClient;
    const isSequential = newSectionIsSequential;
    setAddingSection(null);
    setNewSectionName("");
    setNewSectionIsClient(false);
    setNewSectionIsSequential(false);
    if (!trimmed) return;
    try {
      const existing = projectSectionsMap.get(projectId) || [];
      const maxIdx = existing.reduce((m, s) => Math.max(m, s.list_index), -1);
      const created = await api.createProjectSection(projectId, {
        name: trimmed,
        section_type: isClient ? "client_tasks" : "firm_tasks",
        is_client_facing: isClient,
        list_index: maxIdx + 1,
        is_sequential: isSequential,
      });
      setProjectSectionsMap((prev) => {
        const secs = [...(prev.get(projectId) || []), created];
        return new Map(prev).set(projectId, secs);
      });
      if (isClient) {
        api.getSectionReminder(created.id).then(cfg => {
          if (cfg) setSectionReminderMap(m => ({ ...m, [created.id]: { is_active: cfg.is_active, days: cfg.days, schedule_type: cfg.schedule_type ?? "interval", monthly_day: cfg.monthly_day } }));
        }).catch(() => {});
      }
    } catch {
      toast({ title: "Failed to add section", variant: "destructive" });
    }
  };

  const handleToggleSectionType = async (sectionId: string, projectId: string, currentType: string) => {
    const newType = currentType === "client_tasks" ? "firm_tasks" : "client_tasks";
    try {
      await api.updateTaskSection(sectionId, { section_type: newType, is_client_facing: newType === "client_tasks" });
      setProjectSectionsMap((prev) => {
        const secs = (prev.get(projectId) || []).map(s => s.id === sectionId ? { ...s, section_type: newType } : s);
        return new Map(prev).set(projectId, secs);
      });
      if (newType === "client_tasks") {
        api.getSectionReminder(sectionId).then(cfg => {
          if (cfg) setSectionReminderMap(m => ({ ...m, [sectionId]: { is_active: cfg.is_active, days: cfg.days, schedule_type: cfg.schedule_type ?? "interval", monthly_day: cfg.monthly_day } }));
        }).catch(() => {});
      }
    } catch {
      toast({ title: "Failed to update section type", variant: "destructive" });
    }
  };

  // Toggle "tasks must be done in order" on a section (advisory sequencing +
  // "your turn" notifications — migration 096).
  const handleToggleSequential = async (sectionId: string, projectId: string, current: boolean) => {
    try {
      await api.updateTaskSection(sectionId, { is_sequential: !current });
      setProjectSectionsMap((prev) => {
        const secs = (prev.get(projectId) || []).map(s => s.id === sectionId ? { ...s, is_sequential: !current } : s);
        return new Map(prev).set(projectId, secs);
      });
    } catch {
      toast({ title: "Failed to update section ordering", variant: "destructive" });
    }
  };

  const handleSectionDragStart = (e: React.DragEvent, sectionId: string) => {
    setDragSectionId(sectionId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleSectionDragOver = (e: React.DragEvent, sectionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (sectionId !== dragSectionId) setDragOverSectionId(sectionId);
  };

  const handleSectionDrop = async (e: React.DragEvent, dropSectionId: string, projectId: string) => {
    e.preventDefault();
    e.stopPropagation();
    // A task dropped on a section header / empty section body moves into that section.
    if (dragTaskId) { applyTaskMove(projectId, { sectionId: dropSectionId }); return; }
    if (!dragSectionId || dragSectionId === dropSectionId) { setDragSectionId(null); setDragOverSectionId(null); return; }
    setProjectSectionsMap((prev) => {
      const secs = [...(prev.get(projectId) || [])];
      const fromIdx = secs.findIndex((s) => s.id === dragSectionId);
      const toIdx = secs.findIndex((s) => s.id === dropSectionId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = secs.splice(fromIdx, 1);
      secs.splice(toIdx, 0, moved);
      const updated = secs.map((s, i) => ({ ...s, list_index: i }));
      // Fire-and-forget saves
      updated.forEach((s) => api.updateTaskSection(s.id, { list_index: s.list_index }).catch(() => {}));
      return new Map(prev).set(projectId, updated);
    });
    setDragSectionId(null); setDragOverSectionId(null);
  };

  const handleSectionDragEnd = () => { setDragSectionId(null); setDragOverSectionId(null); };

  // Modal helpers
  const resetForm = () => {
    setFormData({ title: "", account_id: "", status: "open", start_date: "", due_date: "", internal_due_date: "", template_id: "", recurring_pattern: "", next_spawn_date: "", project_type: "", tax_year: "", period_label: "", accounting_period_date: "" });
    setAssignedUsers([]);
    setNewUserId("");
    setNewUserRole("agent");
  };

  const openCreateModal = () => {
    resetForm();
    setEditingProject(null);
    setModalOpen(true);
  };

  const openEditModal = async (project: Project) => {
    try {
      const full = await api.getProject(project.id);
      setEditingProject(full);
      setFormData({
        title: full.title || "",
        account_id: full.account_id || "",
        status: full.status || "open",
        start_date: full.start_date || "",
        due_date: full.due_date || "",
        internal_due_date: full.internal_due_date || "",
        template_id: full.template_id || "",
        recurring_pattern: full.recurring_pattern || "",
        next_spawn_date: full.next_spawn_date || "",
        project_type: full.project_type || "",
        tax_year: full.tax_year ? String(full.tax_year) : "",
        period_label: full.period_label || "",
        accounting_period_date: full.accounting_period_date || "",
      });
      setAssignedUsers(full.assigned_users || []);
      setNewUserId("");
      setNewUserRole("agent");
      setModalOpen(true);
    } catch (err: any) {
      toast({ title: "Error loading project", description: err.message, variant: "destructive" });
    }
  };

  const handleSave = () => {
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (!formData.account_id) {
      toast({ title: "Client is required", variant: "destructive" });
      return;
    }
    const apDate = formData.accounting_period_date || null;
    const apPattern = formData.recurring_pattern || null;
    const payload = {
      title: formData.title.trim(),
      account_id: formData.account_id,
      status: formData.status,
      start_date: formData.start_date || null,
      due_date: formData.due_date || null,
      internal_due_date: formData.internal_due_date || null,
      template_id: formData.template_id || null,
      recurring_pattern: apPattern,
      next_spawn_date: formData.next_spawn_date || null,
      project_type: formData.project_type || null,
      tax_year: formData.tax_year ? parseInt(formData.tax_year) : null,
      period_label: formData.period_label || null,
      accounting_period_date: apDate,
      computed_accounting_period: apDate && apPattern ? computePeriodLabel(apDate, apPattern) : null,
    };
    saveMutation.mutate({ isEdit: !!editingProject, id: editingProject?.id, payload, accountId: formData.account_id });
  };

  const handleDelete = async () => {
    if (!editingProject) return;
    if (!(await askConfirm({
      title: "Delete this project?",
      subject: "project",
      name: editingProject.title,
      body: "will be deleted.",
      lines: buildProjectDeleteWarning(editingProject),
      confirmLabel: "Delete",
      destructive: true,
    }))) return;
    deleteMutation.mutate(editingProject.id);
  };

  const availableUsersForAssignment = allUsers.filter(
    (u) => !assignedUsers.some((au) => au.user_id === u.id)
  );

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Back button — shown when navigating from a client profile */}
        {backAccount && (
          <div>
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground -ml-2" onClick={() => setLocation(`/accounts/${backAccount.id}`)}>
              <ArrowLeft className="w-4 h-4" />
              Back to {backAccount.name}
            </Button>
          </div>
        )}

        {/* Focus mode banner */}
        {focusProjectId && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
            <Maximize2 className="w-4 h-4 shrink-0" />
            <span className="flex-1">Showing single project</span>
            <button
              onClick={() => setFocusProjectId(null)}
              className="text-xs font-medium underline hover:no-underline"
            >
              ← All projects
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-bold">Projects</h1>
          <div className="flex items-center gap-2">
            {user?.role !== "contractor" && (
              <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="button-bulk-projects">
                <Plus className="w-4 h-4 mr-2" />
                For Many Clients
              </Button>
            )}
            <Button onClick={openCreateModal} data-testid="button-new-project">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
            <BulkCreateProjectsDialog open={bulkOpen} onOpenChange={setBulkOpen} />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="flex gap-3 overflow-x-auto pb-1">
          <SummaryCard
            count={summaryStats.dueToday}
            label="Due Today"
            onClick={() => setFilterGroups([{ combinator: "AND", rules: [{ field: "due_date", operator: "is", values: ["today"] }] }])}
            active={filterGroups.some((g) => g.rules.some((r) => r.field === "due_date" && r.values.includes("today")))}
          />
          <SummaryCard
            count={summaryStats.dueThisWeek}
            label="Due This Week"
            onClick={() => setFilterGroups([{ combinator: "AND", rules: [{ field: "due_date", operator: "is", values: ["this_week"] }] }])}
            active={filterGroups.some((g) => g.rules.some((r) => r.field === "due_date" && r.values.includes("this_week")))}
          />
          <SummaryCard
            count={summaryStats.dueNextWeek}
            label="Due Next Week"
            onClick={() => setFilterGroups([{ combinator: "AND", rules: [{ field: "due_date", operator: "is", values: ["next_week"] }] }])}
            active={filterGroups.some((g) => g.rules.some((r) => r.field === "due_date" && r.values.includes("next_week")))}
          />
          <SummaryCard
            count={summaryStats.overdue}
            label="Overdue"
            onClick={() => setFilterGroups([{ combinator: "AND", rules: [{ field: "due_date", operator: "is", values: ["overdue"] }] }])}
            active={filterGroups.some((g) => g.rules.some((r) => r.field === "due_date" && r.values.includes("overdue")))}
          />
        </div>

        <Separator />

        {/* Filter Toolbar */}
        <div className="space-y-3">
          {/* Row 1: search on its own line so it stays full-width and readable */}
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 w-full"
              data-testid="input-search-projects"
            />
          </div>

          {/* Row 2: filters, sort, view, grouping.
              Two columns, not one wrapping row: the controls wrap among themselves on the left
              while the count and Reset View stay pinned top-right. In a single wrapping row they
              were dragged down whenever a control changed width — picking a client group widens
              that dropdown to fit the group's name, which was enough to reflow the whole row. */}
          <div className="flex items-start gap-2">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
          <Button
            variant={projectStatusFilter === "active" ? "default" : "outline"}
            size="sm"
            className="h-9 text-xs"
            onClick={() => setProjectStatusFilter((v) => v === "active" ? "all" : "active")}
          >
            {projectStatusFilter === "active" ? "Active Projects" : "All Projects"}
          </Button>

          <Button
            variant={hideFuture ? "default" : "outline"}
            size="sm"
            className="h-9 text-xs"
            onClick={() => setHideFuture((v) => !v)}
            title="Hide projects with a start date in the future"
          >
            {hideFuture ? "Hide Future" : "Show Future"}
          </Button>

          <Button
            variant={myWorkOnly ? "default" : "outline"}
            size="sm"
            className={`h-9 text-xs ${myWorkOnly ? "bg-violet-600 hover:bg-violet-700 border-violet-600" : ""}`}
            onClick={() => setMyWorkOnly((v) => !v)}
            title="Show only projects where the current task step is assigned to you"
          >
            My Work
          </Button>

          <FilterPresetBar
            presets={filterPresets}
            currentFilters={currentFilters}
            hasActiveFilters={hasCustomView}
            onApply={applyFilters}
            onSave={savePreset}
            onRemove={removePreset}
            onRename={renamePreset}
          />

          {/* Filter Popover */}
          <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5">
                <SlidersHorizontal className="w-3.5 h-3.5" />
                {hasActiveRules(filterGroups)
                  ? `Filter · ${countActiveRules(filterGroups)}`
                  : "Filter"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-3" align="start">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Filters</span>
                {hasActiveRules(filterGroups) && (
                  <button className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setFilterGroups([{ combinator: "AND", rules: [] }])}>
                    Clear all
                  </button>
                )}
              </div>

              <div className="max-h-[60vh] overflow-y-auto space-y-4">
                {filterGroups.map((group, gi) => (
                  <div key={gi} className="space-y-2">
                    {/* Group header — only shown when 2+ groups exist */}
                    {filterGroups.length > 1 && (
                      <div className="flex items-center justify-between">
                        <div className="flex rounded-md border overflow-hidden">
                          {(["AND", "OR"] as const).map((c) => (
                            <button key={c}
                              className={`px-2 py-0.5 text-xs transition-colors ${group.combinator === c ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
                              onClick={() => setFilterGroups((prev) => prev.map((g, i) => i === gi ? { ...g, combinator: c } : g))}>
                              {c}
                            </button>
                          ))}
                        </div>
                        <button className="text-muted-foreground hover:text-foreground"
                          onClick={() => setFilterGroups((prev) => prev.filter((_, i) => i !== gi))}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Rules */}
                    {group.rules.map((rule, ri) => (
                      <div key={ri} className="flex items-center gap-1.5">
                        {/* Field picker */}
                        <Select
                          value={rule.field}
                          onValueChange={(v: FilterField) =>
                            setFilterGroups((prev) => prev.map((g, i) =>
                              i === gi ? { ...g, rules: g.rules.map((r, j) => j === ri ? { field: v, operator: "is", values: [] } : r) } : g
                            ))
                          }
                        >
                          <SelectTrigger className="h-7 w-28 text-xs shrink-0"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="status">Status</SelectItem>
                            <SelectItem value="current_assignee">Current Assignee</SelectItem>
                            <SelectItem value="assignee">Any Assignee</SelectItem>
                            <SelectItem value="client">Client</SelectItem>
                            <SelectItem value="type">Type</SelectItem>
                            <SelectItem value="year">Year</SelectItem>
                            <SelectItem value="month">Month</SelectItem>
                            <SelectItem value="tag">Tag</SelectItem>
                            <SelectItem value="due_date">Due Date</SelectItem>
                            <SelectItem value="recurring">Recurring</SelectItem>
                            {clientGroups.length > 0 && <SelectItem value="client_group">Client Group</SelectItem>}
                          </SelectContent>
                        </Select>

                        {/* Operator picker */}
                        <Select
                          value={rule.operator}
                          onValueChange={(v: FilterOperator) =>
                            setFilterGroups((prev) => prev.map((g, i) =>
                              i === gi ? { ...g, rules: g.rules.map((r, j) => j === ri ? { ...r, operator: v, values: [] } : r) } : g
                            ))
                          }
                        >
                          <SelectTrigger className="h-7 w-20 text-xs shrink-0"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {rule.field === "due_date" ? (
                              <>
                                <SelectItem value="is">is</SelectItem>
                                <SelectItem value="is_empty">is empty</SelectItem>
                                <SelectItem value="is_not_empty">is not empty</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="is">is</SelectItem>
                                <SelectItem value="is_not">is not</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>

                        {/* Value control */}
                        {(rule.operator === "is" || rule.operator === "is_not") && (
                          <FilterMultiSelect
                            field={rule.field}
                            values={rule.values}
                            onChange={(vals) =>
                              setFilterGroups((prev) => prev.map((g, i) =>
                                i === gi ? { ...g, rules: g.rules.map((r, j) => j === ri ? { ...r, values: vals } : r) } : g
                              ))
                            }
                            allUsers={allUsers}
                            accounts={accounts}
                            allTags={allTags}
                            clientGroups={clientGroups}
                          />
                        )}

                        {/* Remove rule */}
                        <button className="text-muted-foreground hover:text-foreground shrink-0 ml-auto"
                          onClick={() =>
                            setFilterGroups((prev) => prev.map((g, i) =>
                              i === gi ? { ...g, rules: g.rules.filter((_, j) => j !== ri) } : g
                            ))
                          }>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    {/* Empty group hint */}
                    {group.rules.length === 0 && filterGroups.length === 1 && (
                      <p className="text-xs text-muted-foreground py-1">No filters active. Click + Add Filter below.</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom actions */}
              <div className="border-t mt-3 pt-3 flex flex-col gap-1.5">
                <button
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    setFilterGroups((prev) => prev.map((g, i) =>
                      i === prev.length - 1 ? { ...g, rules: [...g.rules, { field: "status", operator: "is", values: [] }] } : g
                    ))
                  }
                >
                  <Plus className="w-3 h-3" /> Add Filter
                </button>
                {hasActiveRules(filterGroups) && (
                  <button
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setFilterGroups((prev) => [...prev, { combinator: "AND", rules: [{ field: "status", operator: "is", values: [] }] }])}
                  >
                    <Plus className="w-3 h-3" /> Add Filter Group
                  </button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={sortPopoverOpen} onOpenChange={setSortPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5">
                <ArrowUpDown className="w-3.5 h-3.5" />
                Sort{sortRules.length > 0 ? ` · ${sortRules.length}` : ""}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="start">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort By</span>
                {sortRules.length > 0 && (
                  <button className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setSortRules([{ field: "due_date", dir: "asc" }])}>
                    Clear all
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {sortRules.map((rule, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Select value={rule.field} onValueChange={(v: SortField) =>
                      setSortRules(prev => prev.map((r, i) => i === idx ? { ...r, field: v } : r))}>
                      <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="due_date">Due Date</SelectItem>
                        <SelectItem value="client">Client Name</SelectItem>
                        <SelectItem value="title">Project Title</SelectItem>
                        <SelectItem value="type">Type</SelectItem>
                        <SelectItem value="year">Tax Year</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="start_date">Start Date</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex rounded-md border overflow-hidden shrink-0">
                      {(["due_date", "year", "start_date"].includes(rule.field)
                        ? [["asc", "1→9"], ["desc", "9→1"]] as const
                        : [["asc", "A→Z"], ["desc", "Z→A"]] as const
                      ).map(([d, label]) => (
                        <button key={d}
                          className={`px-2 py-1 text-xs transition-colors ${rule.dir === d ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
                          onClick={() => setSortRules(prev => prev.map((r, i) => i === idx ? { ...r, dir: d as "asc" | "desc" } : r))}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <button className="text-muted-foreground hover:text-foreground shrink-0"
                      onClick={() => setSortRules(prev => prev.filter((_, i) => i !== idx))}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setSortRules(prev => [...prev, { field: "due_date", dir: "asc" }])}>
                <Plus className="w-3 h-3" /> Add Sort
              </button>
            </PopoverContent>
          </Popover>

          {/* View: Table (flat) vs List (accordion) */}
          <div className="flex items-center rounded-md border h-9 overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-2.5 h-full text-xs font-medium inline-flex items-center gap-1 transition-colors ${viewMode === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              title="Flat table view"
            >
              <Table2 className="w-3.5 h-3.5" /> Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-2.5 h-full text-xs font-medium inline-flex items-center gap-1 border-l transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              title="Grouped accordion view"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
          </div>

          {/* Client Group quick filter — sets a client_group rule in the shared filter state */}
          {clientGroups.length > 0 && (
            <Select
              value={activeClientGroupId ?? "all"}
              onValueChange={(v) => {
                setFilterGroups((prev) => {
                  const cleaned = prev.map((g) => ({ ...g, rules: g.rules.filter((r) => r.field !== "client_group") }));
                  const base: FilterGroup[] = cleaned.length ? cleaned : [{ combinator: "AND", rules: [] }];
                  if (v === "all") return base;
                  const newRule: FilterRule = { field: "client_group", operator: "is", values: [v] };
                  return base.map((g, i) => i === 0 ? { ...g, rules: [...g.rules, newRule] } : g);
                });
              }}
            >
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs" data-testid="select-client-group">
                <SelectValue placeholder="All client groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All client groups</SelectItem>
                {clientGroups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={groupBy} onValueChange={(v: typeof groupBy) => { setGroupBy(v); setExpandedGroupsAlt(new Set()); setGroupBy2("none"); setExpandedGroupsAlt2(new Set()); }}>
            <SelectTrigger className="h-9 w-auto min-w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client">By Client</SelectItem>
              <SelectItem value="current_assignee">By Current Assignee</SelectItem>
              <SelectItem value="status">By Status</SelectItem>
              <SelectItem value="type">By Type</SelectItem>
              <SelectItem value="assignee">By Any Assignee</SelectItem>
              <SelectItem value="year">By Year</SelectItem>
              <SelectItem value="period">By Period</SelectItem>
            </SelectContent>
          </Select>

          {groupBy !== "client" && (
            <Select value={groupBy2} onValueChange={(v: typeof groupBy2) => { setGroupBy2(v); setExpandedGroupsAlt2(new Set()); }}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs">
                <SelectValue placeholder="then by…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No sub-group</SelectItem>
                {groupBy !== "status" && <SelectItem value="status">then by Status</SelectItem>}
                {groupBy !== "type" && <SelectItem value="type">then by Type</SelectItem>}
                {groupBy !== "current_assignee" && <SelectItem value="current_assignee">then by Current Assignee</SelectItem>}
                {groupBy !== "assignee" && <SelectItem value="assignee">then by Any Assignee</SelectItem>}
                {groupBy !== "year" && <SelectItem value="year">then by Year</SelectItem>}
                {groupBy !== "period" && <SelectItem value="period">then by Period</SelectItem>}
              </SelectContent>
            </Select>
          )}

          </div>

          {/* Pinned right, outside the wrapping column so it never gets pushed down. */}
          <div className="flex items-center gap-2 shrink-0 h-9">
            {hasCustomView && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 text-xs gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950"
                onClick={resetView}
                title="Return filters, sort, grouping and layout to their defaults"
              >
                <X className="w-3.5 h-3.5" />
                Reset View
              </Button>
            )}
            <span className="text-sm text-muted-foreground whitespace-nowrap">{displayedProjects.length} projects</span>
          </div>
          </div>
        </div>

        {/* Accordion */}
        {displayedProjects.length === 0 ? (
          <div className="border rounded-lg bg-card">
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Briefcase className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No projects found</p>
              <p className="text-sm">Adjust your filters or create a new project</p>
            </div>
          </div>
        ) : viewMode === "table" ? (
          <ProjectsTable
            projects={tableProjects}
            groups={tableGroups}
            expandedGroups={expandedGroupsAlt}
            onToggleGroup={handleTableToggleGroup}
            sortColumn={colSort.col}
            sortDir={colSort.dir}
            onSortColumn={handleSortColumn}
            onOpenProject={handleTableOpenProject}
            onOpenChat={handleTableOpenChat}
            renderAssignees={renderAssigneeAvatars}
            renderTags={renderProjectTags}
            renderWorkflowBadge={renderWorkflowBadge}
          />
        ) : (
          <div className="space-y-2">
            {(groupBy === "client"
              ? groupedProjects.map((c): { key: string; label: string; projects: Project[] } => ({
                  key: c.account_id, label: c.account_name, projects: c.projects,
                }))
              : groupedByAlt
            ).map((group) => {
              const isCollapsed = groupBy === "client"
                ? !forceExpandedAccounts.has(group.key) && collapsedClients.has(group.key)
                : !expandedGroupsAlt.has(group.key);
              const openCount = group.projects.filter(
                (p) => p.status !== "closed" && p.status !== "completed"
              ).length;

              return (
                <div key={group.key} data-account-id={group.key} className="border rounded-lg overflow-hidden bg-card">
                  {/* Group header */}
                  <div className="w-full flex items-center justify-between px-2 py-2.5 bg-muted/50 hover:bg-muted/70 transition-colors">
                    <div className="flex items-center gap-1 min-w-0">
                      <button
                        onClick={() => groupBy === "client"
                          ? handleToggleClient(group.key, group.projects)
                          : setExpandedGroupsAlt((prev) => { const n = new Set(prev); n.has(group.key) ? n.delete(group.key) : n.add(group.key); return n; })
                        }
                        className="shrink-0 p-1 rounded hover:bg-muted/60 transition-colors"
                        title={isCollapsed ? "Expand" : "Collapse"}
                      >
                        <ChevronRight
                          className={`w-4 h-4 text-foreground/60 shrink-0 transition-transform duration-150 ${isCollapsed ? "" : "rotate-90"}`}
                        />
                      </button>
                      <span className="font-bold text-sm truncate min-w-0">{group.label}</span>
                      {groupBy === "client" && <CopyBtn value={group.label} />}
                      {groupBy === "client" && (
                        <a
                          href={`/accounts/${group.key}`}
                          className="text-muted-foreground hover:text-primary transition-colors shrink-0 p-0.5 rounded hover:bg-muted"
                          title="Open client profile"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-3">
                      {openCount > 0 && (
                        <Badge variant="secondary" className="text-xs">{openCount} open</Badge>
                      )}
                      <Badge variant="outline" className="text-xs">{group.projects.length} projects</Badge>
                    </div>
                  </div>

                  {/* Project rows */}
                  {!isCollapsed && (
                    <div>
                      {(groupBy2 !== "none" && groupBy !== "client"
                        ? (groupedByCompound.find((o) => o.key === group.key)?.subgroups ?? []).flatMap((subgroup) => {
                            const innerKey = `${group.key}:${subgroup.key}`;
                            const isInnerCollapsed = !expandedGroupsAlt2.has(innerKey);
                            const innerOpenCount = subgroup.projects.filter((p) => p.status !== "closed" && p.status !== "completed").length;
                            return [
                              { type: "subgroup-header" as const, subgroup, innerKey, isInnerCollapsed, innerOpenCount },
                              ...(isInnerCollapsed ? [] : subgroup.projects.map((p) => ({ type: "project" as const, project: p }))),
                            ];
                          })
                        : group.projects.map((p) => ({ type: "project" as const, project: p }))
                      ).map((item) => {
                        if (item.type === "subgroup-header") {
                          return (
                            <div
                              key={`subhdr:${item.innerKey}`}
                              className="flex items-center gap-2 pl-8 pr-4 py-1.5 bg-muted/20 border-t cursor-pointer hover:bg-muted/40 transition-colors"
                              onClick={() => setExpandedGroupsAlt2((prev) => { const n = new Set(prev); n.has(item.innerKey) ? n.delete(item.innerKey) : n.add(item.innerKey); return n; })}
                            >
                              <ChevronRight className={`w-3.5 h-3.5 text-foreground/50 shrink-0 transition-transform duration-150 ${item.isInnerCollapsed ? "" : "rotate-90"}`} />
                              <span className="text-xs font-semibold text-foreground/70">{item.subgroup.label}</span>
                              {item.innerOpenCount > 0 && <Badge variant="secondary" className="text-xs">{item.innerOpenCount} open</Badge>}
                              <Badge variant="outline" className="text-xs">{item.subgroup.projects.length}</Badge>
                            </div>
                          );
                        }
                        const project = item.project;
                        const progress = taskProgressMap.get(project.id);
                        const isExpanded = expandedProjects.has(project.id);
                        const inlineTasks = projectInlineTasks.get(project.id) || [];
                        const isLoadingInline = inlineTaskLoading.has(project.id);
                        // Use task_assignee_ids (task-level only) — assigned_users also includes all account_members which causes false positives
                        const assignees = (project.task_assignee_ids ?? []).map((uid: string) => ({ user_id: uid }));
                        const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;
                        return (
                          <div key={project.id} className="border-t" data-testid={`row-project-${project.id}`}>
                            {/* Project row — two rows */}
                            <div className="pl-4 pr-4 py-2 hover:bg-muted/10">
                              {/* Row 1: checkbox + title + action buttons */}
                              <div className="flex items-center gap-2">
                                {/* Project completion checkbox */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    // no confirmation for reopening.
                                    if (project.status === "closed") {
                                      saveMutation.mutate({ isEdit: true, id: project.id, payload: { status: "open" } });
                                      return;
                                    }
                                    // Counts come from the server aggregates on the project row, NOT from
                                    // projectInlineTasks — those are only populated once the row has been
                                    // expanded, so a collapsed project used to warn about nothing while the
                                    // close completed every task on it.
                                    const { lines } = buildProjectCloseWarning(project);
                                    setConfirmCompleteProject({ id: project.id, title: project.title, x: e.clientX, y: e.clientY, lines });
                                  }}
                                  className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${project.status === "closed" ? "bg-green-500 border-green-500 hover:bg-green-600" : "border-gray-300 hover:border-green-400"}`}
                                  title={project.status === "closed" ? "Reopen project" : "Mark project complete"}
                                >
                                  {project.status === "closed" && <Check className="w-2.5 h-2.5 text-white" />}
                                </button>

                                {/* Title — expand toggle (plain left-click) / open in new tab (ctrl/cmd/middle/right-click) */}
                                <a
                                  href={`/accounts/${project.account_id}?openProject=${project.id}`}
                                  onClick={(e) => {
                                    // Let the browser handle modified clicks (new tab / new window)
                                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                    e.preventDefault();
                                    handleToggleProjectTasks(project.id);
                                  }}
                                  className={`flex items-center gap-1 font-medium text-sm text-left min-w-0 flex-1 ${project.status === "closed" ? "line-through text-muted-foreground" : "text-primary hover:underline"}`}
                                  data-testid={`link-project-${project.id}`}
                                >
                                  <ChevronRight className={`w-4 h-4 shrink-0 transition-transform duration-150 text-foreground/70 ${isExpanded ? "rotate-90" : ""}`} />
                                  <span className="truncate">
                                    {groupBy !== "client" && (project.account_name || (project as any).accounts?.account_name)
                                      ? `${project.account_name || (project as any).accounts?.account_name} — ${project.title}`
                                      : project.title}
                                  </span>
                                  {/* Period badge — next to title text */}
                                  {(() => {
                                    let periodLabel: string | null = null;
                                    if ((project.recurring_pattern === "weekly" || project.recurring_pattern === "bi-weekly") && (project.accounting_period_date || project.due_date)) {
                                      periodLabel = computePeriodLabel((project.accounting_period_date || project.due_date)!, project.recurring_pattern);
                                    } else if (project.recurring_pattern === "quarterly" && (project.accounting_period_date || project.due_date)) {
                                      periodLabel = computePeriodLabel((project.accounting_period_date || project.due_date)!, "quarterly");
                                    } else if (project.computed_accounting_period || project.tax_year || project.period_label) {
                                      if (project.period_label && project.tax_year) {
                                        periodLabel = yearMonthBadge(project.tax_year, project.period_label);
                                      } else {
                                        periodLabel = project.computed_accounting_period ?? (project.tax_year != null ? String(project.tax_year) : null) ?? project.period_label ?? null;
                                      }
                                    }
                                    if (!periodLabel) return null;
                                    return (
                                      <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0 font-semibold bg-sky-50 text-sky-700 border-sky-300">
                                        {periodLabel}
                                      </Badge>
                                    );
                                  })()}
                                </a>

                                {/* Portal response banner — shown when client has uploaded/commented */}
                                {(projectPortalMap[project.id] ?? 0) > 0 && (
                                  <a
                                    href={`/accounts/${project.account_id}?tab=portal`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500 text-white text-xs font-bold shrink-0 hover:bg-amber-600 transition-colors shadow-sm"
                                    title={`${projectPortalMap[project.id]} client portal response${projectPortalMap[project.id] !== 1 ? "s" : ""} — click to view`}
                                  >
                                    <Paperclip className="w-3.5 h-3.5" />
                                    {projectPortalMap[project.id]} CLIENT {projectPortalMap[project.id] === 1 ? "RESPONSE" : "RESPONSES"}
                                  </a>
                                )}

                                {/* Project-level Auto-Reminders + Automations — derived from client_tasks section */}
                                {(() => {
                                  const sections = projectSectionsMap.get(project.id) ?? [];
                                  const cs = sections.find(s => s.section_type === "client_tasks");
                                  if (!cs) return null;
                                  const rState = sectionReminderMap[cs.id];
                                  const isActive = rState?.is_active ?? false;
                                  return (
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!project.account_id) return;
                                          openReminderModal(cs.id, project.account_id, project.id, project.status, project.deleted_at ?? null);
                                        }}
                                        className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-semibold transition-colors shrink-0 ${isActive ? "text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100" : "text-slate-600 border-slate-300 bg-white hover:border-amber-400 hover:text-amber-700"}`}
                                      >
                                        <Bell className="w-3.5 h-3.5" />
                                        Auto-Reminders{" "}
                                        {isActive ? (
                                          <span className="text-amber-600 font-bold">
                                            ({rState?.schedule_type === "monthly"
                                              ? `day ${rState?.monthly_day ?? "?"}`
                                              : rState?.schedule_type === "section"
                                              ? "on section"
                                              : `every ${rState?.days ?? 3}d`})
                                          </span>
                                        ) : <span className="text-red-500 font-bold">(Off)</span>}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setSectionRulesOpen({ sectionId: cs.id, accountId: project.account_id ?? null }); }}
                                        className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-semibold transition-colors shrink-0 text-violet-700 border-violet-400 bg-violet-50 hover:bg-violet-100"
                                      >
                                        <Zap className="w-3.5 h-3.5" /> Automations
                                      </button>
                                    </div>
                                  );
                                })()}

                                {/* Action buttons */}
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <div className="flex items-center gap-1 mr-1">
                                    {assignees.slice(0, 3).map((au) => {
                                      const u = allUsers.find(x => x.id === au.user_id);
                                      const name = u?.name ?? (au as any).users?.name ?? null;
                                      const email = u?.email ?? (au as any).users?.email ?? null;
                                      if (!name && !email) return null;
                                      return (
                                        <UserAvatar
                                          key={au.user_id ?? (au as any).id}
                                          name={name}
                                          email={email}
                                        />
                                      );
                                    })}
                                    {assignees.length > 3 && (
                                      <span className="text-xs text-muted-foreground">+{assignees.length - 3}</span>
                                    )}
                                  </div>
                                  <TagPicker
                                    appliedTags={project.project_tags ?? []}
                                    onAdd={(tagId) => addTagMutation.mutate({ projectId: project.id, tagId })}
                                    onRemove={(tagId) => removeTagMutation.mutate({ projectId: project.id, tagId })}
                                    align="end"
                                  />
                                  <button
                                    className="h-7 w-7 flex items-center justify-center rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                                    title="Start timer for this project"
                                    onClick={(e) => { e.stopPropagation(); openSheetWithContext(project.account_id ?? "", project.id); }}
                                  >
                                    <Play className="w-4 h-4" />
                                  </button>
                                  <button
                                    className="h-7 w-7 flex items-center justify-center rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                                    title="Messages"
                                    onClick={(e) => { e.stopPropagation(); setMessagesProject({ id: project.id, title: project.title }); }}
                                    data-testid={`button-messages-project-${project.id}`}
                                  >
                                    <MessageSquare className="w-4 h-4" />
                                  </button>
                                  <button
                                    className="h-7 w-7 flex items-center justify-center rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                    title="Edit project"
                                    onClick={(e) => { e.stopPropagation(); openEditModal(project); }}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    className="h-7 w-7 flex items-center justify-center rounded-md bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors"
                                    title="Focus on this project"
                                    onClick={(e) => { e.stopPropagation(); setFocusProjectId(project.id); }}
                                  >
                                    <Maximize2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    className="h-7 w-7 flex items-center justify-center rounded-md bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors"
                                    title="Delete project"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (await askConfirm({
                                        title: "Delete this project?",
                                        subject: "project",
                                        name: project.title,
                                        body: "will be deleted.",
                                        lines: buildProjectDeleteWarning(project),
                                        confirmLabel: "Delete",
                                        destructive: true,
                                      })) {
                                        deleteMutation.mutate(project.id);
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Row 2: metadata — recurring, period, type, next spawn, tags, workflow, progress, due date */}
                              <div className="flex items-center gap-1.5 mt-1 pl-6 flex-wrap">
                                {groupBy !== "client" && (project.account_name || (project as any).accounts?.account_name) && (
                                  <a
                                    href={`/accounts/${project.account_id}`}
                                    className="text-xs font-semibold text-primary hover:underline shrink-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {project.account_name || (project as any).accounts?.account_name}
                                  </a>
                                )}
                                {project.recurring_pattern && (
                                  <span className="inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white shrink-0 capitalize">
                                    ↺ {project.recurring_pattern}
                                  </span>
                                )}
                                {project.recurring_pattern && project.next_spawn_date && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-medium bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 shrink-0">
                                    <span className="text-blue-400">Next:</span>
                                    {(() => { const d = parseISO(project.next_spawn_date); return isNaN(d.getTime()) ? project.next_spawn_date : format(d, "MMM d, yyyy"); })()}
                                  </span>
                                )}
                                {project.project_type && (
                                  <Badge variant="outline" className={`text-xs px-2 py-0 shrink-0 font-semibold ${projectTypeBadgeClasses[project.project_type] ?? "bg-gray-100 text-gray-700 border border-gray-300"}`}>
                                    {project.project_type}
                                  </Badge>
                                )}
                                {isOverdue(project) && project.status !== "closed" && (
                                  <Badge variant="destructive" className="text-xs px-1.5 py-0 shrink-0">Overdue</Badge>
                                )}
                                {(project.project_tags ?? []).map((tag: Tag) => (
                                  <TagChip
                                    key={tag.id}
                                    tag={tag}
                                    onRemove={() => removeTagMutation.mutate({ projectId: project.id, tagId: tag.id })}
                                  />
                                ))}
                                {project.current_step_title && (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded border bg-violet-50 border-violet-200 text-violet-700 shrink-0 max-w-[200px] truncate" title={project.current_step_title}>
                                    ▶ {project.current_step_title}
                                  </span>
                                )}
                                <div className="ml-auto flex items-center gap-3 shrink-0">
                                  {workflowPipelines.length > 0 && (
                                    <WorkflowStatusBadge
                                      project={project}
                                      pipelines={workflowPipelines}
                                      onStatusChange={(pid, sid) => statusMutation.mutate({ projectId: pid, statusId: sid })}
                                      isPending={statusPendingIds.has(project.id)}
                                    />
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    {progress ? `${progress.done}/${progress.total} tasks` : "—"}
                                  </span>
                                  {project.due_date && (() => {
                                    const overdue = isOverdue(project) && project.status !== "closed";
                                    const d = parseISO(project.due_date);
                                    const label = isNaN(d.getTime()) ? project.due_date : format(d, "MMM d, yyyy");
                                    return (
                                      <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 border shrink-0 ${overdue ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                                        <span className={overdue ? "text-rose-400" : "text-slate-400"}>Due</span>
                                        {label}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>

                            {/* Inline task list */}
                            {isExpanded && (
                              <div className="pl-10 pr-4 pb-2.5 pt-1 bg-muted/5 border-t border-dashed border-border/50">
                                {isLoadingInline && (
                                  <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Loading tasks…
                                  </div>
                                )}
                                {!isLoadingInline && inlineTasks.length === 0 && addingTaskForProject !== project.id && (projectSectionsMap.get(project.id) ?? []).length === 0 && (
                                  <p className="text-xs text-muted-foreground py-1">No tasks</p>
                                )}
                                {(() => {
                                  // Use the dedicated sections map as primary source; fall back to task-derived if not loaded yet
                                  const loadedSections = projectSectionsMap.get(project.id);
                                  // Build task buckets by section_id
                                  const taskBucket = new Map<string, any[]>();
                                  for (const task of inlineTasks) {
                                    const key = task.section_id || "__none__";
                                    if (!taskBucket.has(key)) taskBucket.set(key, []);
                                    taskBucket.get(key)!.push(task);
                                  }
                                  // Sorted section list — from sections map, or derived from tasks if sections not loaded
                                  const sortedSections: TaskSection[] = loadedSections
                                    ? [...loadedSections].sort((a, b) =>
                                        (a.section_type === "client_tasks" ? 0 : 1) - (b.section_type === "client_tasks" ? 0 : 1) ||
                                        a.list_index - b.list_index)
                                    : (() => {
                                        const derived = new Map<string, TaskSection>();
                                        for (const task of inlineTasks) {
                                          const sec = task.task_sections;
                                          if (sec && !derived.has(sec.id)) derived.set(sec.id, sec as TaskSection);
                                        }
                                        return [...derived.values()].sort((a, b) =>
                                          (a.section_type === "client_tasks" ? 0 : 1) - (b.section_type === "client_tasks" ? 0 : 1) ||
                                          a.list_index - b.list_index);
                                      })();
                                  return (
                                    <>
                                      {sortedSections.map((section, sectionIdx) => {
                                        const secTasks = taskBucket.get(section.id) || [];
                                        const secDone = secTasks.filter((t: any) => t.status === "done" || t.status === "completed").length;
                                        const isAddingHere = addingTaskForProject === project.id && addingTaskForSection === section.id;
                                        const isRenaming = renamingSection?.sectionId === section.id;
                                        const isSectionCollapsed = collapsedSections.has(`${project.id}:${section.id}`);
                                        const isClientSec = (section as any).section_type === "client_tasks";
                                        return (
                                          <div
                                            key={section.id}
                                            draggable
                                            onDragStart={(e) => { e.stopPropagation(); handleSectionDragStart(e, section.id); }}
                                            onDragOver={(e) => { e.stopPropagation(); handleSectionDragOver(e, section.id); }}
                                            onDrop={(e) => { e.stopPropagation(); handleSectionDrop(e, section.id, project.id); }}
                                            onDragEnd={(e) => { e.stopPropagation(); handleSectionDragEnd(); }}
                                            className={`mb-1.5 ${sectionIdx > 0 ? "border-t border-border pt-1.5" : ""} ${dragOverSectionId === section.id && dragSectionId !== section.id ? "border-t-2 border-primary pt-0.5" : ""} ${dragSectionId === section.id ? "opacity-40" : ""}`}
                                          >
                                            {/* Section header */}
                                            <div className={`flex items-center gap-1 py-0.5 group/sec rounded px-0.5 ${isClientSec ? "bg-blue-50/60" : ""}`}>
                                              <span
                                                className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                                                title="Drag to reorder sections"
                                                onMouseDown={(e) => e.stopPropagation()}
                                              >
                                                <GripVertical className="w-3 h-3" />
                                              </span>
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); toggleProjectSection(project.id, section.id); }}
                                                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                                                title={isSectionCollapsed ? "Expand section" : "Collapse section"}
                                              >
                                                {isSectionCollapsed
                                                  ? <ChevronRight className="w-3 h-3" />
                                                  : <ChevronDown className="w-3 h-3" />}
                                              </button>
                                              {isRenaming ? (
                                                <input
                                                  autoFocus
                                                  className="flex-1 text-[10px] font-semibold uppercase tracking-wider border-b border-primary bg-transparent outline-none min-w-0"
                                                  value={renamingSection!.name}
                                                  onChange={(e) => setRenamingSection({ sectionId: section.id, name: e.target.value, projectId: project.id })}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleRenameSection(section.id, renamingSection!.name, project.id);
                                                    if (e.key === "Escape") setRenamingSection(null);
                                                  }}
                                                  onBlur={() => handleRenameSection(section.id, renamingSection!.name, project.id)}
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); toggleProjectSection(project.id, section.id); }}
                                                  className={`text-[10px] font-semibold uppercase tracking-wider min-w-0 max-w-[200px] truncate text-left transition-colors ${isClientSec ? "text-blue-700" : "text-muted-foreground hover:text-foreground"}`}
                                                >
                                                  {section.name}
                                                </button>
                                              )}
                                              <button
                                                type="button"
                                                title={(section as any).is_sequential ? "Tasks must be done in order — click for any order" : "Tasks may be done in any order — click to require order (notifies the next assignee as each task completes)"}
                                                onClick={(e) => { e.stopPropagation(); handleToggleSequential(section.id, project.id, !!(section as any).is_sequential); }}
                                                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded border font-bold shadow-sm transition-colors shrink-0 ${(section as any).is_sequential ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700" : "border-amber-500 bg-amber-400 text-amber-950 hover:bg-amber-300"}`}
                                              >
                                                <ListOrdered className="w-3.5 h-3.5" />
                                                Ordered & Notified? {(section as any).is_sequential ? "(Yes)" : "(No)"}
                                              </button>
                                              <button
                                                type="button"
                                                title={isClientSec ? "Client tasks section — click to convert to firm tasks" : "Firm tasks section — click to mark as client tasks"}
                                                onClick={(e) => { e.stopPropagation(); handleToggleSectionType(section.id, project.id, (section as any).section_type); }}
                                                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded border font-semibold shadow-sm transition-colors shrink-0 ${isClientSec ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700" : "border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-600"}`}
                                              >
                                                <Users className="w-3.5 h-3.5" />
                                                For Client? {isClientSec ? "(Yes)" : "(No)"}
                                              </button>
                                              {isClientSec && (() => {
                                                // Per-task truth: the portal only lists client tasks with sent_at set,
                                                // so any open task without it is invisible to the client.
                                                const openClient = secTasks.filter((t: any) => t.status !== "done" && t.status !== "completed");
                                                if (openClient.length === 0) return null;
                                                const unsent = openClient.filter((t: any) => !t.sent_at).length;
                                                if (unsent > 0) {
                                                  return (
                                                    <span
                                                      className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 border border-red-300 text-red-700 font-semibold shrink-0"
                                                      title={`${unsent} open client task${unsent === 1 ? "" : "s"} not sent yet — hidden from the client portal until sent`}
                                                    >
                                                      Not Sent
                                                    </span>
                                                  );
                                                }
                                                const lastSent = openClient.map((t: any) => t.sent_at as string).sort().pop();
                                                let label = "";
                                                try { const d = parseISO(lastSent!); label = isNaN(d.getTime()) ? "" : ` ${format(d, "MMM d")}`; } catch { /* keep bare label */ }
                                                return (
                                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 border border-green-300 text-green-700 font-medium shrink-0">
                                                    Sent{label}
                                                  </span>
                                                );
                                              })()}
                                              <div className="flex items-center gap-1.5 ml-auto shrink-0 flex-wrap">
                                                <span className="text-[10px] text-muted-foreground/70">{secDone}/{secTasks.length}</span>
                                                {isClientSec && secTasks.filter((t: any) => t.status !== "done" && t.status !== "completed").length > 0 && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); if (project.account_id) setNotifyModal({ projectId: project.id, projectTitle: project.title, accountId: project.account_id }); }}
                                                    className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors font-semibold shrink-0"
                                                  >
                                                    ✉ Send Now
                                                  </button>
                                                )}
                                                {isClientSec && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (!project.account_id) return;
                                                      openReminderModal(section.id, project.account_id, project.id, project.status, project.deleted_at ?? null);
                                                    }}
                                                    className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-semibold transition-colors shrink-0 ${
                                                      sectionReminderMap[section.id]?.is_active
                                                        ? "text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100"
                                                        : "text-slate-600 border-slate-300 bg-white hover:border-amber-400 hover:text-amber-700"
                                                    }`}
                                                  >
                                                    <Bell className="w-3.5 h-3.5" />
                                                    Auto-Reminders{" "}
                                                    {sectionReminderMap[section.id]?.is_active ? (
                                                      <span className="text-amber-600 font-bold">
                                                        ({sectionReminderMap[section.id]?.schedule_type === "monthly"
                                                          ? `day ${sectionReminderMap[section.id]?.monthly_day ?? "?"}`
                                                          : sectionReminderMap[section.id]?.schedule_type === "section"
                                                          ? "on section"
                                                          : `every ${sectionReminderMap[section.id]?.days ?? 3}d`})
                                                      </span>
                                                    ) : (
                                                      <span className="text-red-500 font-bold">(Off)</span>
                                                    )}
                                                  </button>
                                                )}
                                                <button
                                                  className="text-muted-foreground hover:text-primary transition-colors"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAddingTaskForProject(project.id);
                                                    setAddingTaskForSection(section.id);
                                                    setNewTaskTitle("");
                                                    // ENG-86 follow-up: a fully-completed section may be auto-collapsed
                                                    // on load, which otherwise hides the add-task input this button opens.
                                                    setCollapsedSections(prev => {
                                                      const key = `${project.id}:${section.id}`;
                                                      if (!prev.has(key)) return prev;
                                                      const n = new Set(prev);
                                                      n.delete(key);
                                                      return n;
                                                    });
                                                  }}
                                                  title="Add task to this section"
                                                >
                                                  <ListPlus className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                  type="button"
                                                  title="Section automation rules"
                                                  className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors font-semibold shrink-0"
                                                  onClick={(e) => { e.stopPropagation(); setSectionRulesOpen({ sectionId: section.id, accountId: project.account_id ?? null }); }}
                                                >
                                                  <Zap className="w-3.5 h-3.5" />
                                                  Automations
                                                </button>
                                                <button
                                                  className="text-blue-600 hover:text-blue-800 transition-colors"
                                                  title="Rename section"
                                                  onClick={(e) => { e.stopPropagation(); setRenamingSection({ sectionId: section.id, name: section.name, projectId: project.id }); }}
                                                >
                                                  <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                  className="text-rose-600 hover:text-rose-800 transition-colors"
                                                  title="Delete section (tasks become unsectioned)"
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (await askConfirm({
                                                      title: "Delete this section?",
                                                      subject: "section",
                                                      name: section.name,
                                                      body: "will be deleted.",
                                                      lines: secTasks.length > 0
                                                        ? [`Its ${secTasks.length} task${secTasks.length === 1 ? "" : "s"} are kept, and become unsectioned.`]
                                                        : [],
                                                      confirmLabel: "Delete",
                                                      destructive: true,
                                                    })) handleDeleteSection(section.id, project.id);
                                                  }}
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                              </div>
                                            </div>
                                            {/* Tasks in this section */}
                                            {!isSectionCollapsed && secTasks.map((task: any) => (
                                              <div
                                                key={task.id}
                                                draggable
                                                onDragStart={(e) => { e.stopPropagation(); handleTaskDragStart(e, task.id, project.id); }}
                                                onDragOver={(e) => { e.stopPropagation(); handleTaskDragOver(e, task.id); }}
                                                onDrop={(e) => { e.stopPropagation(); handleTaskDrop(e, task.id, project.id); }}
                                                onDragEnd={(e) => { e.stopPropagation(); handleTaskDragEnd(); }}
                                                className={`flex items-center gap-0.5 rounded transition-colors ${dragOverTaskId === task.id && dragProjectId === project.id ? "border-t-2 border-primary" : ""} ${dragTaskId === task.id ? "opacity-40" : ""}`}
                                              >
                                                <span className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground px-0.5" title="Drag to reorder">
                                                  <GripVertical className="w-3 h-3" />
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                  <TaskDetailRow
                                                    task={task as ProjectTask}
                                                    onStatusChange={(id, done) => handleTaskStatusChange(project.id, id, done)}
                                                    onUpdate={(id, updates) => handleTaskUpdate(project.id, id, updates)}
                                                    onDelete={(id) => handleTaskDelete(project.id, id)}
                                                    accountId={project.account_id ?? undefined}
                                                    portalResponseCount={taskPortalMap[task.id] ?? 0}
                                                    sequentialBlockedOn={(() => {
                                                      // Advisory: in an "in order" section, a task waits on the earliest
                                                      // incomplete root task above it (secTasks is list_index-sorted).
                                                      if (!(section as any).is_sequential) return null;
                                                      if (task.status === "done" || task.status === "completed" || task.parent_task_id) return null;
                                                      const earlier = secTasks.find((t: any) =>
                                                        t.id !== task.id && !t.parent_task_id &&
                                                        (t.list_index ?? 0) < (task.list_index ?? 0) &&
                                                        t.status !== "done" && t.status !== "completed");
                                                      return earlier ? earlier.title : null;
                                                    })()}
                                                  />
                                                </div>
                                              </div>
                                            ))}
                                            {/* Section-level inline add */}
                                            {!isSectionCollapsed && isAddingHere && (
                                              <div className="flex items-center gap-1.5 mt-1 mb-1">
                                                <input
                                                  autoFocus
                                                  className="flex-1 text-xs border border-border rounded px-2 py-1 bg-background outline-none focus:ring-1 focus:ring-primary"
                                                  placeholder="Task title…"
                                                  value={newTaskTitle}
                                                  onChange={(e) => setNewTaskTitle(e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter") handleAddTask(project.id, section.id);
                                                    if (e.key === "Escape") { setAddingTaskForProject(null); setAddingTaskForSection(null); setNewTaskTitle(""); }
                                                  }}
                                                  onBlur={() => handleAddTask(project.id, section.id)}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {/* Unsectioned tasks — separated from grouped sections */}
                                      {sortedSections.length > 0 && (taskBucket.get("__none__") || []).length > 0 && (
                                        <div className="border-t border-border mt-2 mb-1.5 pt-1.5">
                                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/60 px-0.5 select-none">Ungrouped</span>
                                        </div>
                                      )}
                                      {(taskBucket.get("__none__") || []).map((task: any) => (
                                        <div
                                          key={task.id}
                                          draggable
                                          onDragStart={(e) => handleTaskDragStart(e, task.id, project.id)}
                                          onDragOver={(e) => handleTaskDragOver(e, task.id)}
                                          onDrop={(e) => handleTaskDrop(e, task.id, project.id)}
                                          onDragEnd={handleTaskDragEnd}
                                          className={`flex items-center gap-0.5 rounded transition-colors ${dragOverTaskId === task.id && dragProjectId === project.id ? "border-t-2 border-primary" : ""} ${dragTaskId === task.id ? "opacity-40" : ""}`}
                                        >
                                          <span className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground px-0.5" title="Drag to reorder">
                                            <GripVertical className="w-3 h-3" />
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <TaskDetailRow
                                              task={task as ProjectTask}
                                              onStatusChange={(id, done) => handleTaskStatusChange(project.id, id, done)}
                                              onUpdate={(id, updates) => handleTaskUpdate(project.id, id, updates)}
                                              onDelete={(id) => handleTaskDelete(project.id, id)}
                                              accountId={project.account_id ?? undefined}
                                              portalResponseCount={taskPortalMap[task.id] ?? 0}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  );
                                })()}
                                {/* Add task row (bottom fallback) */}
                                {addingTaskForProject === project.id && addingTaskForSection === null ? (
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <input
                                      autoFocus
                                      className="flex-1 text-xs border border-border rounded px-2 py-1 bg-background outline-none focus:ring-1 focus:ring-primary"
                                      placeholder="Task title…"
                                      value={newTaskTitle}
                                      onChange={(e) => setNewTaskTitle(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleAddTask(project.id, null);
                                        if (e.key === "Escape") { setAddingTaskForProject(null); setNewTaskTitle(""); }
                                      }}
                                      onBlur={() => handleAddTask(project.id, null)}
                                    />
                                  </div>
                                ) : (
                                  <button
                                    className="flex items-center gap-1 mt-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    onClick={() => { setAddingTaskForProject(project.id); setAddingTaskForSection(null); }}
                                  >
                                    <Plus className="w-3 h-3" /> Add task
                                  </button>
                                )}
                                {/* Add section row */}
                                {addingSection === project.id ? (
                                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed border-border/40">
                                    <input
                                      autoFocus
                                      className="flex-1 text-xs border border-border rounded px-2 py-1 bg-background outline-none focus:ring-1 focus:ring-primary"
                                      placeholder="Section name…"
                                      value={newSectionName}
                                      onChange={(e) => setNewSectionName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleAddSection(project.id);
                                        if (e.key === "Escape") { setAddingSection(null); setNewSectionName(""); setNewSectionIsClient(false); }
                                      }}
                                      onBlur={() => handleAddSection(project.id)}
                                    />
                                    <button
                                      type="button"
                                      onMouseDown={(e) => { e.preventDefault(); setNewSectionIsClient(v => !v); }}
                                      title={newSectionIsClient ? "Client tasks section — click to switch to firm" : "Firm tasks section — click to switch to client"}
                                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors shrink-0 ${newSectionIsClient ? "border-blue-400 bg-blue-50 text-blue-700 font-semibold" : "border-slate-200 bg-white text-slate-400 hover:border-blue-300"}`}
                                    >
                                      <Users className="w-3 h-3" />
                                      {newSectionIsClient ? "Client" : "Firm"}
                                    </button>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => { e.preventDefault(); setNewSectionIsSequential(v => !v); }}
                                      title={newSectionIsSequential ? "Tasks must be done in order — click for any order" : "Tasks may be done in any order — click to require order"}
                                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors shrink-0 ${newSectionIsSequential ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200 bg-white text-slate-400 hover:border-emerald-300"}`}
                                    >
                                      <ListOrdered className="w-3 h-3" />
                                      {newSectionIsSequential ? "In order" : "Any order"}
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="flex items-center gap-1 mt-2 pt-2 border-t border-dashed border-border/40 w-full text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                                    onClick={() => { setAddingSection(project.id); setNewSectionName(""); setNewSectionIsClient(false); setNewSectionIsSequential(false); }}
                                    title="Add a new section to this project"
                                  >
                                    <Plus className="w-3 h-3" /> Add section
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Create / Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) setModalOpen(false); }}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProject ? "Edit Project" : "Create Project"}</DialogTitle>
              <DialogDescription>
                {editingProject ? "Update project details and team assignments." : "Add a new project for a client."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Template (only if templates exist) — shown first so title can auto-fill */}
              {!editingProject && templates.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="template">Template</Label>
                  <Select
                    value={formData.template_id || "none"}
                    onValueChange={(v) => {
                      const tpl = templates.find((t) => t.id === v);
                      setFormData({
                        ...formData,
                        template_id: v === "none" ? "" : v,
                        title: tpl ? tpl.title : formData.title,
                        project_type: tpl?.project_type ?? formData.project_type,
                      });
                    }}
                  >
                    <SelectTrigger id="template" data-testid="select-project-template">
                      <SelectValue placeholder="No template" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[280px]">
                      <SelectItem value="none">No template</SelectItem>
                      {templates.filter((t) => t.id).map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title <span className="text-destructive">*</span></Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Project title"
                  autoFocus={!templates.length || !!editingProject}
                  data-testid="input-project-title"
                />
              </div>

              {/* Client */}
              <div className="space-y-2">
                <Label htmlFor="account">Client <span className="text-destructive">*</span></Label>
                <SearchableSelect
                  value={formData.account_id}
                  onValueChange={(v) => setFormData({ ...formData, account_id: v })}
                  placeholder="Search clients..."
                  options={accounts.filter((a) => a.id).map((a) => ({ value: a.id, label: a.account_name }))}
                />
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger id="status" data-testid="select-project-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dates — Start / Client Due / Internal Due */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date">Client Due Date</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    data-testid="input-project-due-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="internal_due_date">Internal Due</Label>
                  <Input
                    id="internal_due_date"
                    type="date"
                    value={formData.internal_due_date}
                    onChange={(e) => setFormData({ ...formData, internal_due_date: e.target.value })}
                  />
                </div>
              </div>


              {/* Recurring Pattern */}
              <div className="space-y-2">
                <Label htmlFor="recurring_pattern">Recurrence</Label>
                <Select
                  value={formData.recurring_pattern || "none"}
                  onValueChange={(v) => setFormData({ ...formData, recurring_pattern: v === "none" ? "" : v, next_spawn_date: v === "none" ? "" : formData.next_spawn_date })}
                >
                  <SelectTrigger id="recurring_pattern" data-testid="select-project-recurring">
                    <SelectValue placeholder="One-time" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Accounting Period Picker — week range for weekly/bi-weekly, date for others */}
              {formData.recurring_pattern && formData.recurring_pattern !== "none" && formData.recurring_pattern !== "daily" && (
                <div className="space-y-2">
                  <Label>Accounting Period</Label>
                  {(formData.recurring_pattern === "weekly" || formData.recurring_pattern === "bi-weekly") ? (
                    <div className="border rounded-md p-2 w-fit">
                      <Calendar
                        mode="single"
                        selected={formData.accounting_period_date ? new Date(formData.accounting_period_date + "T12:00:00") : undefined}
                        onSelect={(day) => {
                          if (day) setFormData({ ...formData, accounting_period_date: day.toISOString().split("T")[0] });
                        }}
                      />
                      {formData.accounting_period_date ? (
                        <p className="text-sm text-center mt-1 font-medium text-blue-700">
                          {computePeriodLabel(formData.accounting_period_date, formData.recurring_pattern)}
                        </p>
                      ) : (
                        <p className="text-sm text-center mt-1 text-muted-foreground">Click any day to set the period start</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Input
                        type="date"
                        value={formData.accounting_period_date || ""}
                        onChange={(e) => setFormData({ ...formData, accounting_period_date: e.target.value })}
                      />
                      {formData.accounting_period_date && (
                        <p className="text-sm text-muted-foreground">
                          Label: <span className="font-medium text-foreground">{computePeriodLabel(formData.accounting_period_date, formData.recurring_pattern)}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Project Type + Year + Month */}
              <div className="space-y-2">
                <Label>Project Type</Label>
                <Select
                  value={formData.project_type || "none"}
                  onValueChange={(v) => setFormData({ ...formData, project_type: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="1040">1040</SelectItem>
                    <SelectItem value="1120S">1120S</SelectItem>
                    <SelectItem value="1065">1065</SelectItem>
                    <SelectItem value="1120">1120</SelectItem>
                    <SelectItem value="1041">1041</SelectItem>
                    <SelectItem value="990">990</SelectItem>
                    <SelectItem value="Bookkeeping">Bookkeeping</SelectItem>
                    <SelectItem value="Financial Statements">Financial Statements</SelectItem>
                    <SelectItem value="Property Tax">Property Tax</SelectItem>
                    <SelectItem value="Sales Tax">Sales Tax</SelectItem>
                    <SelectItem value="Payroll">Payroll</SelectItem>
                    <SelectItem value="Advisory">Advisory</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Year — always shown when a type is selected */}
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select
                    value={formData.tax_year || "none"}
                    onValueChange={(v) => setFormData({ ...formData, tax_year: v === "none" ? "" : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Year..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {[2026, 2025, 2024, 2023, 2022].map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Month — for period-based types (bookkeeping, payroll, etc.) */}
                {formData.project_type && ["Bookkeeping","Financial Statements","Payroll","Sales Tax","Property Tax"].includes(formData.project_type) ? (
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select
                      value={formData.period_label || "none"}
                      onValueChange={(v) => setFormData({ ...formData, period_label: v === "none" ? "" : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Month..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        <SelectItem value="01">01 — January</SelectItem>
                        <SelectItem value="02">02 — February</SelectItem>
                        <SelectItem value="03">03 — March</SelectItem>
                        <SelectItem value="04">04 — April</SelectItem>
                        <SelectItem value="05">05 — May</SelectItem>
                        <SelectItem value="06">06 — June</SelectItem>
                        <SelectItem value="07">07 — July</SelectItem>
                        <SelectItem value="08">08 — August</SelectItem>
                        <SelectItem value="09">09 — September</SelectItem>
                        <SelectItem value="10">10 — October</SelectItem>
                        <SelectItem value="11">11 — November</SelectItem>
                        <SelectItem value="12">12 — December</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : <div />}
              </div>

              {/* Next Spawn Date — edit only, shown when recurring pattern is set */}
              {editingProject && formData.recurring_pattern && formData.recurring_pattern !== "none" && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="next_spawn_date">Next Spawn Date</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => setFormData({ ...formData, recurring_pattern: "", next_spawn_date: "" })}
                      data-testid="button-stop-recurring"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Stop recurring
                    </Button>
                  </div>
                  <Input
                    id="next_spawn_date"
                    type="date"
                    value={formData.next_spawn_date || ""}
                    onChange={(e) => setFormData({ ...formData, next_spawn_date: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    "Stop recurring" clears the schedule so no new copies spawn — existing projects are untouched. Save to apply.
                  </p>
                </div>
              )}

              {/* Team section — edit only */}
              {editingProject && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <h3 className="font-semibold text-sm">Team</h3>
                    </div>

                    {assignedUsers.length > 0 && (
                      <div className="space-y-1.5">
                        {assignedUsers.map((au) => (
                          <div key={au.id} className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">
                                {au.users?.name || au.users?.email || "Unknown"}
                              </p>
                              <p className="text-xs text-muted-foreground capitalize">{au.role}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeUserMutation.mutate({ projectId: editingProject.id, userId: au.user_id })}
                              disabled={removeUserMutation.isPending}
                              data-testid={`button-remove-user-${au.user_id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {availableUsersForAssignment.length > 0 && (
                      <div className="flex gap-2">
                        <Select value={newUserId} onValueChange={setNewUserId}>
                          <SelectTrigger className="flex-1" data-testid="select-add-user">
                            <SelectValue placeholder="Select user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableUsersForAssignment.map((u) => (
                              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={newUserRole} onValueChange={setNewUserRole}>
                          <SelectTrigger className="w-28" data-testid="select-user-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="reviewer">Reviewer</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={() => addUserMutation.mutate({ projectId: editingProject.id, userId: newUserId, role: newUserRole })}
                          disabled={!newUserId || addUserMutation.isPending}
                          data-testid="button-add-user"
                        >
                          Add
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              {editingProject && (
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  data-testid="button-delete-project"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="button-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                data-testid="button-save-project"
              >
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingProject ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {messagesProject && (
          <ProjectMessagesSheet
            open={!!messagesProject}
            onClose={() => setMessagesProject(null)}
            projectId={messagesProject.id}
            projectTitle={messagesProject.title}
          />
        )}
        <SectionAutomationsModal
          sectionId={sectionRulesOpen?.sectionId ?? null}
          onClose={() => setSectionRulesOpen(null)}
          accountId={sectionRulesOpen?.accountId}
        />
        {notifyModal && (
          <SendNotificationModal
            projectId={notifyModal.projectId}
            projectTitle={notifyModal.projectTitle}
            accountId={notifyModal.accountId}
            onClose={() => setNotifyModal(null)}
            onSent={() => {
              // The send stamps sent_at server-side — refetch so the Sent/Not Sent badge updates.
              const pid = notifyModal.projectId;
              api.getProjectTasks(pid)
                .then(tasks => setProjectInlineTasks(prev => new Map(prev).set(pid, tasks)))
                .catch(() => {});
            }}
          />
        )}
        <Dialog open={!!reminderModal} onOpenChange={(open) => { if (!open) { setReminderModal(null); setReminderConfig(null); setReminderSavedCfg(null); } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Bell className="w-4 h-4 text-amber-600" /> Auto-Reminders</DialogTitle>
              <DialogDescription>Automatically email client when tasks are outstanding.</DialogDescription>
            </DialogHeader>
            {reminderLoading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : reminderConfig !== null ? (
              <div className="space-y-3 text-sm">
                <label className="flex items-center gap-2 cursor-pointer font-medium">
                  <input type="checkbox" checked={reminderConfig.is_active} onChange={e => setReminderConfig(c => c ? { ...c, is_active: e.target.checked } : c)} className="w-4 h-4" />
                  Auto-Reminders Active
                </label>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Schedule</label>
                  <div className="flex gap-3 flex-wrap">
                    {(["interval", "monthly", "section"] as const).map(t => (
                      <label key={t} className="flex items-center gap-1 cursor-pointer text-xs">
                        <input
                          type="radio"
                          name="reminder-modal-schedule"
                          value={t}
                          checked={reminderConfig.schedule_type === t}
                          onChange={() => setReminderConfig(c => c ? { ...c, schedule_type: t } : c)}
                        />
                        {t === "interval" ? "Every N days" : t === "monthly" ? "Monthly (day of month)" : "When section completes"}
                      </label>
                    ))}
                  </div>
                </div>
                {reminderConfig.schedule_type === "interval" && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Send every</label>
                    <select className="w-full border rounded px-2 py-1 text-sm" value={reminderConfig.days} onChange={e => setReminderConfig(c => c ? { ...c, days: Number(e.target.value) } : c)}>
                      {[1,2,3,5,7,10,14,21,30].map(d => <option key={d} value={d}>{d} days</option>)}
                    </select>
                  </div>
                )}
                {reminderConfig.schedule_type === "monthly" && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">On day (of each month)</label>
                    <select className="w-full border rounded px-2 py-1 text-sm" value={reminderConfig.monthly_day} onChange={e => setReminderConfig(c => c ? { ...c, monthly_day: Number(e.target.value) } : c)}>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                )}
                {reminderConfig.schedule_type === "section" && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Fire when this firm section completes</label>
                    <select className="w-full border rounded px-2 py-1 text-sm" value={reminderConfig.trigger_section_id} onChange={e => setReminderConfig(c => c ? { ...c, trigger_section_id: e.target.value } : c)}>
                      <option value="">— select a section —</option>
                      {(reminderModal ? (projectSectionsMap.get(reminderModal.projectId) ?? []) : [])
                        .filter(s => s.section_type !== "client_tasks" && s.id !== reminderModal?.sectionId)
                        .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Send to (comma-separated emails)</label>
                  <Input
                    value={reminderConfig.contacts}
                    onChange={e => {
                      const value = e.target.value;
                      // Entering contacts implies the reminder should be active — matches the
                      // account-page panel and the server default (active whenever contacts present).
                      const hasContacts = value.split(",").map(s => s.trim()).filter(Boolean).length > 0;
                      setReminderConfig(c => c ? { ...c, contacts: value, is_active: hasContacts ? true : c.is_active } : c);
                    }}
                    placeholder="client@email.com"
                  />
                </div>
                {reminderSavedCfg?.last_sent_at && (
                  <p className="text-xs text-muted-foreground">Last sent: {(() => { try { const d = parseISO(reminderSavedCfg.last_sent_at); return isNaN(d.getTime()) ? reminderSavedCfg.last_sent_at : format(d, "MMM d, yyyy"); } catch { return reminderSavedCfg.last_sent_at; } })()}</p>
                )}
                {(() => {
                  // Same guard as the account-page panel: a reminder whose project is closed
                  // keeps its armed next_send_at forever, so show why it is dormant instead of
                  // a date the 9 AM pass will skip (server/index.ts:567).
                  //
                  // The status is carried on reminderModal, set from the row that opened it, and is
                  // deliberately NOT looked up in `projects`: that query is active-only by default
                  // (see the useQuery above) while a closed project still renders through the separate
                  // `focusedProjectOverride` state. A lookup therefore missed exactly the closed
                  // projects this display exists for, and the helper's unknown-project fall-back then
                  // printed the date we were suppressing. Guarded by projects.reminder-status.test.ts.
                  const nextDisplay = reminderNextDisplay(
                    reminderSavedCfg,
                    reminderModal ? { status: reminderModal.projectStatus, deleted_at: reminderModal.projectDeletedAt } : undefined,
                  );
                  if (nextDisplay.kind === "hidden") return null;
                  if (nextDisplay.kind === "paused") {
                    return <p className="text-xs text-muted-foreground italic">Paused — {nextDisplay.reason}</p>;
                  }
                  return <p className="text-xs text-muted-foreground">Next: {(() => { try { const d = parseISO(nextDisplay.at); return isNaN(d.getTime()) ? nextDisplay.at : format(d, "MMM d, yyyy"); } catch { return nextDisplay.at; } })()}</p>;
                })()}
              </div>
            ) : null}
            <DialogFooter className="flex gap-2 pt-2">
              <Button variant="outline" size="sm" disabled={reminderSending || !reminderConfig?.contacts?.trim()} onClick={async () => {
                if (!reminderModal || !reminderConfig) return;
                setReminderSending(true);
                try {
                  // Persist whatever is typed in the contacts field FIRST — the server sends to
                  // its SAVED contacts, so an unsaved edit must be committed before Send Now fires.
                  const contacts = reminderConfig.contacts.split(",").map(s => s.trim()).filter(Boolean);
                  await api.upsertSectionReminder(reminderModal.sectionId, {
                    days: reminderConfig.days,
                    is_active: reminderConfig.is_active || contacts.length > 0,
                    contacts,
                    schedule_type: reminderConfig.schedule_type,
                    monthly_day: reminderConfig.schedule_type === "monthly" ? reminderConfig.monthly_day : null,
                    trigger_section_id: reminderConfig.schedule_type === "section" ? reminderConfig.trigger_section_id || null : null,
                  });
                  const res = await api.sendSectionReminderNow(reminderModal.sectionId) as { taskCount?: number; emailed_to?: string[]; error?: string };
                  api.getSectionReminder(reminderModal.sectionId).then(cfg => setReminderSavedCfg(cfg)).catch(() => {});
                  if (res.emailed_to && res.emailed_to.length === 0) {
                    toast({ title: "Nothing was sent", description: res.error || "No contacts could be emailed.", variant: "destructive" });
                  } else if (res.emailed_to) {
                    toast({ title: `Sent to ${res.emailed_to.join(", ")}` });
                  } else {
                    toast({ title: "Reminder sent" });
                  }
                } catch { toast({ title: "Failed to send", variant: "destructive" }); }
                setReminderSending(false);
              }}>{reminderSending ? "Sending…" : "Send Now"}</Button>
              <Button size="sm" disabled={reminderSaving || !reminderConfig} onClick={async () => {
                if (!reminderModal || !reminderConfig) return;
                setReminderSaving(true);
                const contacts = reminderConfig.contacts.split(",").map(s => s.trim()).filter(Boolean);
                try {
                  // Pass monthly_day/trigger_section_id through even though this modal has no UI
                  // for them — omitting them made this save wipe schedules configured on the
                  // account page (the server nulls any field it doesn't receive).
                  const saved = await api.upsertSectionReminder(reminderModal.sectionId, {
                    days: reminderConfig.days,
                    is_active: reminderConfig.is_active,
                    contacts,
                    schedule_type: reminderConfig.schedule_type,
                    monthly_day: reminderConfig.schedule_type === "monthly" ? reminderConfig.monthly_day : null,
                    trigger_section_id: reminderConfig.schedule_type === "section" ? reminderConfig.trigger_section_id || null : null,
                  });
                  setSectionReminderMap(m => ({ ...m, [reminderModal.sectionId]: { is_active: saved.is_active, days: saved.days, schedule_type: saved.schedule_type ?? "interval", monthly_day: saved.monthly_day } }));
                  setReminderSavedCfg(saved);
                  toast({ title: saved.is_active ? "Auto-reminders activated" : "Reminder saved (inactive)" });
                  setReminderModal(null); setReminderConfig(null);
                } catch { toast({ title: "Failed to save", variant: "destructive" }); }
                setReminderSaving(false);
              }}>{reminderSaving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* confirm marking a project complete — from the checkbox, or from completing its last task */}
        <CompleteConfirmDialog
          open={!!confirmCompleteProject}
          onOpenChange={(open) => { if (!open) setConfirmCompleteProject(null); }}
          subject="project"
          name={confirmCompleteProject?.title}
          lines={confirmCompleteProject?.lines ?? []}
          onConfirm={() => {
            const target = confirmCompleteProject;
            setConfirmCompleteProject(null);
            if (target) completeProjectConfirmed(target);
          }}
        />
      </div>
    </Layout>
  );
}
